import Hls from "hls.js";
import { logger } from "~/lib/logger";
import {
    isMeditationVerboseDebugEnabled,
    meditationDebugLog,
    meditationDebugWarn,
    meditationVerboseDebugLog,
    meditationVerboseDebugWarn,
} from "~/features/meditations/utils/browser-debug";
import type {
    BufferedRangeTuple,
    MeditationPlaybackBreakLogData,
    MeditationPlaybackBreakType,
    MeditationPlaybackSessionContext,
    MeditationPlaybackType,
    StreamingState,
    StreamingAction,
} from "./types";
import { GAP_DURATION } from "./types";
import { StallDetector, type StallStats } from "./stall-detector";

// AIDEV-NOTE: MIME type for fMP4/AAC - universal browser MediaSource support
// This matches the server-side ffmpeg output format
const FMP4_AAC_MIME_TYPE = 'audio/mp4; codecs="mp4a.40.2"';

// Minimum buffered duration (seconds) before starting playback
const MIN_BUFFER_DURATION = 0.5;
// AIDEV-NOTE: The audible threshold is the end of the silent runway, not
// guaranteed speech. Require a bit of progress beyond it so fullscreen doesn't
// switch while Safari is still stalled on the last silent segment.
const NATIVE_HLS_AUDIBLE_PROGRESS_EPSILON_SECONDS = 0.5;
// AIDEV-NOTE: Native HLS startup should have some narration runway beyond the
// silent primer before play() is invoked, or Safari stalls at the threshold.
const NATIVE_HLS_STARTUP_BUFFER_MARGIN_SECONDS = 1.5;
const HLS_FULL_BUFFER_EPSILON_SECONDS = 0.75;
const HLS_JS_FORWARD_BUFFER_TARGET_SECONDS = Number.POSITIVE_INFINITY;
const HLS_JS_PRE_PLAY_BUFFER_SECONDS = 5;

// AIDEV-NOTE: Scale pre-play buffer thresholds based on observed connection
// speed. navigator.connection.downlink is reported in Mbps; we bump the
// startup buffer significantly on slow connections so the player waits for
// enough audio to survive jitter before starting. Returns 1 if the API
// is unavailable (Safari iOS) so we don't penalise those users. See Gina
// 2026-04-14 (11 Mbps over Zoom) for the motivating case.
function getNetworkBufferMultiplier(): number {
    if (typeof navigator === "undefined") return 1;
    const conn = (
        navigator as Navigator & {
            connection?: { downlink?: number };
        }
    ).connection;
    const downlink = conn?.downlink;
    if (typeof downlink !== "number" || !Number.isFinite(downlink)) return 1;
    if (downlink >= 5) return 1;
    if (downlink >= 2) return 2;
    return 3;
}
const HLS_JS_PRE_PLAY_WARMUP_TIMEOUT_MS = 15_000;
// AIDEV-NOTE: Native HLS has no ENDLIST event we can observe from the media
// element; Safari re-polls the manifest and <audio>.duration grows. Debounce
// duration discovery until the value has been stable for this long — proxy
// for "manifest finalized". Direct-URL MP3 and hls.js use authoritative
// signals instead.
const NATIVE_HLS_DURATION_STABLE_MS = 2_000;
const HLS_JS_MEDITATION_START_POSITION_SECONDS = 0;
const HLS_JS_EARLY_BREAK_SUPPRESSION_MS = 3_000;
const HLS_JS_BREAK_DEDUP_AFTER_STALL_MS = 2_000;
// AIDEV-NOTE: Raised from 2000→6000ms after Datadog analysis showed meditations
// playing 98.7-98.9% were triggering false "unexpected_end" errors (4-5.5s short
// of expected duration due to natural HLS stream ending behavior).
const UNEXPECTED_END_TOLERANCE_MS = 6000;
const BREAK_DEDUP_WINDOW_MS = 1000;
const STALL_LOG_MIN_MS = 85;

// AIDEV-NOTE: Test hook for Playwright timeout tests. Allows overriding
// the 60s startup timeout to make tests run in seconds, not minutes.
function getStartupTimeoutMs(): number {
    if (
        typeof window !== "undefined" &&
        (window as Window & { __MEDITATION_STARTUP_TIMEOUT_OVERRIDE?: number })
            .__MEDITATION_STARTUP_TIMEOUT_OVERRIDE
    ) {
        return (
            window as Window & {
                __MEDITATION_STARTUP_TIMEOUT_OVERRIDE?: number;
            }
        ).__MEDITATION_STARTUP_TIMEOUT_OVERRIDE!;
    }
    return 60_000;
}

// Threshold in bytes to trigger READY notification (~50KB ≈ few seconds of audio)
const READY_THRESHOLD_BYTES = 50 * 1024;

/**
 * Check if MediaSource API is available and supports our audio format.
 * AIDEV-NOTE: MediaSource is NOT available on iOS Safari < 17.1 (and limited even then).
 * This check prevents ReferenceError on Safari/iOS where MediaSource is undefined.
 */
function isMediaSourceSupported(): boolean {
    if (typeof MediaSource === "undefined") {
        return false;
    }
    try {
        return MediaSource.isTypeSupported(FMP4_AAC_MIME_TYPE);
    } catch {
        return false;
    }
}

function supportsNativeHlsPlayback(audio: HTMLAudioElement): boolean {
    // AIDEV-NOTE: Chrome on macOS reports canPlayType("application/vnd.apple.mpegurl")
    // as "maybe" but cannot actually demux CMAF/fMP4 HLS segments, resulting in
    // DEMUXER_ERROR_COULD_NOT_PARSE. Only trust native HLS on Safari/WebKit.
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS/.test(ua);
    if (!isSafari) {
        return false;
    }

    try {
        return audio.canPlayType("application/vnd.apple.mpegurl") !== "";
    } catch {
        return false;
    }
}

/**
 * Configuration for AudioController
 */
export interface AudioControllerConfig {
    dispatch: React.Dispatch<StreamingAction>;
    getState: () => StreamingState;
    // AIDEV-NOTE: Force fallback mode for testing (e.g., ?forceFallback=1 URL param)
    forceFallback?: boolean;
}

interface HlsErrorData {
    fatal?: boolean;
    details?: string;
    type?: string;
    response?: {
        code?: number;
    };
    url?: string;
}

/**
 * AudioController encapsulates all imperative audio operations.
 *
 * AIDEV-NOTE: This class eliminates stale closure issues by:
 * 1. Using stable `dispatch` from useReducer (never changes identity)
 * 2. Reading current state via `getState()` which returns the latest ref value
 *
 * AIDEV-NOTE: Uses MediaSource API for streaming playback.
 * Audio plays as chunks arrive (after MIN_BUFFER_DURATION buffered).
 * Safari/iOS compatible via fMP4/AAC format from server.
 *
 * AIDEV-NOTE: Fallback mode for browsers without MediaSource (iOS Safari < 17.1):
 * - In fallback mode, chunks are ignored during generation
 * - When complete event arrives with audioUrl, playFromUrl() is called
 * - Audio plays from the cached file URL (progressive download)
 */
export class AudioController {
    private introAudio: HTMLAudioElement;
    private mainAudio: HTMLAudioElement;
    private dispatch: React.Dispatch<StreamingAction>;
    private getState: () => StreamingState;
    // AIDEV-NOTE: Force fallback mode for testing (e.g., ?forceFallback=1 URL param)
    private _forceFallback: boolean;

    // Gap timer
    private gapTimeoutId: number | null = null;

    // AIDEV-NOTE: Fallback mode for browsers without MediaSource (iOS Safari < 17.1)
    // In fallback mode, we wait for the complete audioUrl instead of streaming chunks
    private _fallbackMode = false;
    // AIDEV-NOTE: Native HLS fallback on Safari starts near the live edge of the
    // primed silent runway. Track that initial offset so the UI clock starts at 0.
    private _nativeHlsPlayback = false;
    private _hlsJsPlayback = false;
    // AIDEV-NOTE: Once-per-session guard for DURATION_DISCOVERED. Prevents
    // the UI from flickering as <audio>.duration grows during HLS manifest
    // re-polling. Reset in resetFallbackPlaybackProfile.
    private _durationDiscoveredDispatched = false;
    private _nativeHlsDurationStableTimeout: ReturnType<
        typeof setTimeout
    > | null = null;
    private _nativeHlsStartOffsetSeconds: number | null = null;
    private _nativeHlsAudibleStartThresholdSeconds = 0;
    private _nativeHlsAudibleStartLogged = false;
    private _hlsJsInstance: Hls | null = null;
    private _playbackSessionContext: MeditationPlaybackSessionContext | null =
        null;
    private _playbackStartedAtMs: number | null = null;
    private _loggedPlayingSessionId: string | null = null;
    private _expectedPauseUntilMs = 0;
    private _stallStartedAtMs: number | null = null;
    private _lastObservedStallEndedAtMs: number | null = null;
    private _lastBreakLogSignature = "";
    private _lastBreakLogAtMs = 0;
    // AIDEV-NOTE: EVO-1813 — debounced UI stall indicator (isStalled), separate
    // from the telemetry stall tracking above. `this.dispatch` is read lazily
    // inside the arrow callbacks, so the field initializer order is safe.
    private stallDetector = new StallDetector({
        onStallDetected: () => this.dispatch({ type: "STALL_DETECTED" }),
        onStallCleared: () => this.dispatch({ type: "STALL_CLEARED" }),
    });

    // AIDEV-NOTE: Alerting design — cumulative stall stats for the playback-end
    // beacon, so the server can tell a real unrecovered stall (interrupt) from
    // benign user abandonment. Read at end-of-playback, before stop()/reset().
    getStallStats(): StallStats {
        return this.stallDetector.getStats();
    }

    // Intro MediaSource streaming state
    private introMediaSource: MediaSource | null = null;
    private introSourceBuffer: SourceBuffer | null = null;
    private introPendingChunks: Uint8Array[] = [];
    private introTotalBytes = 0;
    private introStreamEnded = false;
    private hasNotifiedIntroReady = false;
    private introPlaybackStarted = false;

    // Main MediaSource streaming state
    private mainMediaSource: MediaSource | null = null;
    private mainSourceBuffer: SourceBuffer | null = null;
    private mainPendingChunks: Uint8Array[] = [];
    private mainTotalBytes = 0;
    private mainStreamEnded = false;
    private hasNotifiedMainReady = false;
    private mainPlaybackStarted = false;

    // AIDEV-NOTE: Signed URL fallback for when MediaSource can't decode the cached format.
    // Old meditations are cached as MP3 but MediaSource expects fMP4/AAC.
    // When play() fails with NotSupportedError, we fall back to this URL.
    private _audioUrl: string | null = null;

    // AIDEV-NOTE: Single-flight guard for direct streaming — prevents iOS WebKit
    // from triggering duplicate server-side generation via repeated requests.
    private _directStreamingUrl: string | null = null;
    private _mainAudioInstanceId = 0;

    // AIDEV-NOTE: When true, chunks are buffered into MediaSource but playback
    // is not started automatically. Used by generate() to start SSE generation
    // in the background while the user is on the intention screen.
    // Cleared by unsuppressPlayback() when the user clicks Begin/Skip.
    private _playbackSuppressed = false;
    // AIDEV-NOTE: Safari can emit follow-on media errors after a play()
    // rejection that we already classified as NotAllowedError. Keep those
    // startup errors from clobbering the tap-to-listen recovery state.
    private _awaitingUserGestureRecovery = false;

    constructor(config: AudioControllerConfig) {
        this.dispatch = config.dispatch;
        this.getState = config.getState;
        this._forceFallback = config.forceFallback ?? false;

        this.introAudio = new Audio();
        this.mainAudio = new Audio();
        this._mainAudioInstanceId = 1;
        this.introAudio.preload = "auto";
        this.mainAudio.preload = "auto";

        // AIDEV-NOTE: Add audio elements to DOM so they can be queried by tests
        // and potentially controlled by browser dev tools. They're hidden but functional.
        this.introAudio.style.display = "none";
        this.mainAudio.style.display = "none";
        this.introAudio.setAttribute("data-meditation-audio", "intro");
        this.mainAudio.setAttribute("data-meditation-audio", "main");
        this.mainAudio.setAttribute(
            "data-audio-instance-id",
            this.getMainAudioInstanceLabel(),
        );
        document.body.appendChild(this.introAudio);
        document.body.appendChild(this.mainAudio);

        this.setupEventListeners();
    }

    setPlaybackSessionContext(
        context: MeditationPlaybackSessionContext | null,
    ) {
        this._playbackSessionContext = context;
        this._playbackStartedAtMs = null;
        this._loggedPlayingSessionId = null;
        this._stallStartedAtMs = null;
        this._lastObservedStallEndedAtMs = null;
        this._lastBreakLogSignature = "";
        this._lastBreakLogAtMs = 0;
    }

    getPlaybackTelemetrySnapshot() {
        const audio = this.getActiveAudioElement();
        const timeSinceStartCalledMs =
            this._playbackSessionContext?.startCalledAtMs === null ||
            this._playbackSessionContext?.startCalledAtMs === undefined
                ? null
                : Date.now() - this._playbackSessionContext.startCalledAtMs;

        let seekableEnd: number | null = null;
        try {
            if (audio.seekable.length > 0) {
                seekableEnd = audio.seekable.end(audio.seekable.length - 1);
            }
        } catch {
            seekableEnd = null;
        }
        const rawDuration = audio.duration;
        const duration = Number.isFinite(rawDuration) ? rawDuration : null;

        return {
            playbackType: this.getPlaybackType(),
            timeSincePlaybackStartMs: this.getTimeSincePlaybackStartMs(),
            timeSinceStartCalledMs,
            currentTime: this.getAudioCurrentTime(audio),
            readyState: audio.readyState,
            networkState: audio.networkState,
            paused: audio.paused,
            bufferedRanges: this.getBufferedRanges(audio),
            seekableEnd,
            duration,
        };
    }

    private getPlaybackType(): MeditationPlaybackType {
        if (this._hlsJsPlayback) {
            return "hls-js";
        }
        if (this._nativeHlsPlayback) {
            return "native-hls";
        }
        if (this._fallbackMode) {
            return "url-fallback";
        }
        if (this.mainMediaSource || this.introMediaSource) {
            return "mediasource";
        }
        return "unknown";
    }

    private getTimeSincePlaybackStartMs() {
        return this._playbackStartedAtMs === null
            ? null
            : Date.now() - this._playbackStartedAtMs;
    }

    private getActiveAudioElement() {
        const state = this.getState();
        if (!this._fallbackMode && state.phase === "intro") {
            return this.introAudio;
        }
        return this.mainAudio;
    }

    private getAudioCurrentTime(audio: HTMLAudioElement) {
        if (audio === this.mainAudio) {
            return this.normalizeFallbackMainTime(this.mainAudio.currentTime);
        }
        return audio.currentTime;
    }

    private getBufferedRanges(audio: HTMLAudioElement): BufferedRangeTuple[] {
        const ranges: BufferedRangeTuple[] = [];
        try {
            for (let index = 0; index < audio.buffered.length; index += 1) {
                ranges.push([
                    audio.buffered.start(index),
                    audio.buffered.end(index),
                ]);
            }
        } catch {
            return [];
        }
        return ranges;
    }

    private expectPause(ms = 750) {
        this._expectedPauseUntilMs = Math.max(
            this._expectedPauseUntilMs,
            Date.now() + ms,
        );
    }

    private isExpectedPause() {
        return Date.now() <= this._expectedPauseUntilMs;
    }

    private maybeLogFirstStablePlaying(audio: HTMLAudioElement) {
        if (this._playbackStartedAtMs === null) {
            this._playbackStartedAtMs = Date.now();
        }

        const playbackSessionId =
            this._playbackSessionContext?.playbackSessionId ?? null;
        if (!playbackSessionId) {
            return;
        }

        if (this._loggedPlayingSessionId === playbackSessionId) {
            return;
        }

        const timeSinceStartCalledMs =
            this._playbackSessionContext?.startCalledAtMs === null ||
            this._playbackSessionContext?.startCalledAtMs === undefined
                ? null
                : Date.now() - this._playbackSessionContext.startCalledAtMs;

        this._loggedPlayingSessionId = playbackSessionId;
        logger
            .withContext({
                event: "meditation.playback.playing",
                playbackSessionId,
                meditationId: this._playbackSessionContext?.meditationId,
                sessionId: this._playbackSessionContext?.sessionId,
                partInteractionId:
                    this._playbackSessionContext?.partInteractionId,
                voice: this._playbackSessionContext?.voice,
                pauseMultiplier:
                    this._playbackSessionContext?.pauseMultiplier ?? 1,
                timeSinceStartCalledMs,
                playbackType: this.getPlaybackType(),
                currentTime: this.getAudioCurrentTime(audio),
            })
            .info("meditation.playback.playing");
    }

    private shouldSuppressBreakLog(
        breakType: MeditationPlaybackBreakType,
        payload: Partial<MeditationPlaybackBreakLogData>,
        currentTime: number | null,
    ) {
        const signature = JSON.stringify({
            playbackSessionId:
                this._playbackSessionContext?.playbackSessionId ?? null,
            breakType,
            phase: this.getState().phase,
            currentTime:
                currentTime === null ? null : Math.round(currentTime * 10) / 10,
            errorName: payload.errorName ?? null,
            mediaErrorCode: payload.mediaErrorCode ?? null,
            hlsErrorDetails: payload.hlsErrorDetails ?? null,
            httpStatus: payload.httpStatus ?? null,
            responseMessage: payload.responseMessage ?? null,
        });
        const now = Date.now();
        if (
            signature === this._lastBreakLogSignature &&
            now - this._lastBreakLogAtMs < BREAK_DEDUP_WINDOW_MS
        ) {
            return true;
        }

        this._lastBreakLogSignature = signature;
        this._lastBreakLogAtMs = now;
        return false;
    }

    private logBreak(
        level: "warn" | "error",
        breakType: MeditationPlaybackBreakType,
        payload: Partial<MeditationPlaybackBreakLogData> = {},
        audio: HTMLAudioElement = this.getActiveAudioElement(),
    ) {
        const context = this._playbackSessionContext;
        const currentTime = this.getAudioCurrentTime(audio);
        const sanitizedPayload =
            breakType === "stall"
                ? payload
                : (() => {
                      const { stallDurationMs: _ignored, ...rest } = payload;
                      return rest;
                  })();
        if (
            this.shouldSuppressBreakLog(
                breakType,
                sanitizedPayload,
                currentTime,
            )
        ) {
            return;
        }

        const eventPayload: MeditationPlaybackBreakLogData = {
            event: "meditation.playback.break",
            breakType,
            playbackSessionId: context?.playbackSessionId ?? null,
            meditationId: context?.meditationId,
            sessionId: context?.sessionId,
            partInteractionId: context?.partInteractionId,
            voice: context?.voice ?? "unknown",
            pauseMultiplier: context?.pauseMultiplier ?? 1,
            phase: this.getState().phase,
            playbackType: this.getPlaybackType(),
            isReconnecting: this.getState().isReconnecting,
            timeSincePlaybackStartMs: this.getTimeSincePlaybackStartMs(),
            timeSinceStartCalledMs:
                context?.startCalledAtMs === null ||
                context?.startCalledAtMs === undefined
                    ? null
                    : Date.now() - context.startCalledAtMs,
            currentTime,
            readyState: audio.readyState,
            networkState: audio.networkState,
            paused: audio.paused,
            bufferedRanges: this.getBufferedRanges(audio),
            ...sanitizedPayload,
        };

        if (level === "error") {
            logger.withContext(eventPayload).error("meditation.playback.break");
            return;
        }

        logger.withContext(eventPayload).warn("meditation.playback.break");
    }

    private failPlayback(message: string) {
        this.stop();
        this.dispatch({
            type: "ERROR",
            message,
        });
    }

    // AIDEV-NOTE: Classifies a startup timeout into one of four buckets and
    // decides whether to extend, dispatch NEEDS_USER_GESTURE, or fail with a
    // user-friendly message. The timeoutClassification field is indexed in Datadog.
    private classifyAndHandleTimeout(options: {
        cleanup: () => void;
        url: string;
        audibleStartThresholdSeconds: number;
        playRequested: boolean;
        playbackStarted: boolean;
        path: "hls-js" | "native-hls" | "direct";
        onExtend: (newTimeoutId: ReturnType<typeof setTimeout>) => void;
        destroyHls?: () => void;
    }): void {
        const snapshot = this.getAudioDebugSnapshot(this.mainAudio);
        const bufferedEnd = this.getBufferedEndSeconds();
        // AIDEV-NOTE: requiredBuffer must mirror the gate that decides when
        // play() is requested for the active path. The hls-js path waits for
        // HLS_JS_PRE_PLAY_BUFFER_SECONDS × network multiplier (capped at
        // duration − epsilon for fully buffered short tracks) — comparing
        // against the smaller native-HLS margin misclassified a still-warming
        // buffer as "gesture-expired" and made the partial-buffer extension
        // path unreachable on hls-js.
        const nativeHlsRequiredBuffer = Math.max(
            options.audibleStartThresholdSeconds +
                NATIVE_HLS_STARTUP_BUFFER_MARGIN_SECONDS,
            NATIVE_HLS_STARTUP_BUFFER_MARGIN_SECONDS,
        );
        const mainDurationSeconds = this.mainAudio.duration;
        const fullBufferTargetSeconds =
            Number.isFinite(mainDurationSeconds) && mainDurationSeconds > 0
                ? Math.max(
                      mainDurationSeconds - HLS_FULL_BUFFER_EPSILON_SECONDS,
                      0,
                  )
                : Number.POSITIVE_INFINITY;
        const hlsJsRequiredBuffer = Math.min(
            HLS_JS_PRE_PLAY_BUFFER_SECONDS * getNetworkBufferMultiplier(),
            fullBufferTargetSeconds,
        );
        const requiredBuffer =
            options.path === "hls-js"
                ? hlsJsRequiredBuffer
                : nativeHlsRequiredBuffer;

        let classification:
            | "gesture-expired"
            | "partial-buffer"
            | "nothing-buffered"
            | "network-error";

        if (
            this.mainAudio.networkState ===
                HTMLMediaElement.NETWORK_NO_SOURCE ||
            (this.mainAudio.readyState === 0 &&
                this.mainAudio.networkState === 3)
        ) {
            classification = "network-error";
        } else if (bufferedEnd >= requiredBuffer && !options.playRequested) {
            classification = "gesture-expired";
        } else if (bufferedEnd > 0) {
            classification = "partial-buffer";
        } else {
            classification = "nothing-buffered";
        }

        const MAX_EXTENSIONS = 1;
        const EXTENSION_TIMEOUT_MS = 30_000;

        // AIDEV-NOTE: Recoverable classifications (gesture-expired, or
        // partial-buffer with extensions remaining) log at warn so front-end
        // error telemetry isn't inflated by paths that recover on their own.
        const canExtend =
            classification === "partial-buffer" &&
            this._timeoutExtensionCount < MAX_EXTENSIONS;
        const recoverable = classification === "gesture-expired" || canExtend;
        const logLevel: "warn" | "error" = recoverable ? "warn" : "error";

        this.logBreak(
            logLevel,
            "startup_timeout",
            {
                errorMessage: `Startup timeout classified as ${classification} (extension ${this._timeoutExtensionCount}/${MAX_EXTENSIONS})`,
                timeoutClassification: classification,
                timeoutExtensionCount: this._timeoutExtensionCount,
                playbackPath: options.path,
                bufferedEnd,
                requiredBuffer,
                playRequested: options.playRequested,
            },
            this.mainAudio,
        );

        const consoleLog = recoverable ? console.warn : console.error;
        consoleLog(`[AudioController] startup timeout classified`, {
            ...snapshot,
            classification,
            extensionCount: this._timeoutExtensionCount,
            path: options.path,
            bufferedEnd,
            requiredBuffer,
            playRequested: options.playRequested,
        });

        const friendlyMessage =
            "We couldn't load your meditation. Our team has been notified — please try again in a moment.";

        switch (classification) {
            case "gesture-expired":
                options.cleanup();
                this.dispatch({ type: "NEEDS_USER_GESTURE" });
                break;

            case "partial-buffer":
                if (this._timeoutExtensionCount < MAX_EXTENSIONS) {
                    this._timeoutExtensionCount++;
                    meditationDebugLog(
                        "[AudioController] extending startup timeout",
                        {
                            extensionCount: this._timeoutExtensionCount,
                            bufferedEnd,
                        },
                    );
                    this.dispatch({ type: "RECONNECTING" });
                    const overrideMs = (
                        window as Window & {
                            __MEDITATION_STARTUP_TIMEOUT_OVERRIDE?: number;
                        }
                    ).__MEDITATION_STARTUP_TIMEOUT_OVERRIDE;
                    const extensionMs =
                        typeof overrideMs === "number"
                            ? Math.min(EXTENSION_TIMEOUT_MS, overrideMs)
                            : EXTENSION_TIMEOUT_MS;
                    const extendedTimeoutId = setTimeout(() => {
                        this.classifyAndHandleTimeout(options);
                    }, extensionMs);
                    options.onExtend(extendedTimeoutId);
                } else {
                    options.cleanup();
                    options.destroyHls?.();
                    this.failPlayback(friendlyMessage);
                }
                break;

            case "nothing-buffered":
            case "network-error":
                options.cleanup();
                options.destroyHls?.();
                this.failPlayback(friendlyMessage);
                break;
        }
    }

    private logMediaError(
        audio: HTMLAudioElement,
        level: "warn" | "error",
        errorMessage: string,
    ) {
        const mediaError = audio.error;
        this.logBreak(
            level,
            "media_error",
            {
                errorMessage,
                mediaErrorCode: mediaError?.code ?? undefined,
                mediaErrorMessage: mediaError?.message ?? undefined,
            },
            audio,
        );
    }

    private logPlayRejected(
        audio: HTMLAudioElement,
        error: unknown,
        level: "warn" | "error",
        errorMessage: string,
    ) {
        const errorName = error instanceof Error ? error.name : undefined;
        if (
            errorName === "AbortError" &&
            this.getState().phase === "idle" &&
            !this.getState().isLoading &&
            !this.getState().isGenerating
        ) {
            return;
        }

        this.logBreak(
            level,
            "play_rejected",
            {
                errorName,
                errorMessage:
                    error instanceof Error ? error.message : errorMessage,
            },
            audio,
        );
    }

    private maybeLogUnexpectedPause(
        audio: HTMLAudioElement,
        errorMessage: string,
    ) {
        const state = this.getState();
        if (
            !state.isPlaying ||
            state.isReconnecting ||
            this.isExpectedPause() ||
            audio.ended
        ) {
            return;
        }

        // AIDEV-NOTE: Severity classification for unexpected pause events.
        // If the user was genuinely mid-meditation (<90% played), this is an error
        // (real interruption). Near the end (≥90%) it's likely a browser quirk.
        // Use normalized currentTime and state.mainDuration (same basis) to avoid
        // native-HLS offset mismatch where getAudioCurrentTime subtracts the
        // live-edge start offset but audio.duration remains raw.
        const currentTime = this.getAudioCurrentTime(audio);
        const normalizedDuration = state.mainDuration;
        const hasValidDuration =
            Number.isFinite(normalizedDuration) && normalizedDuration > 0;
        const playedFraction = hasValidDuration
            ? currentTime / normalizedDuration
            : 0;
        const level = playedFraction < 0.9 ? "error" : "warn";

        this.logBreak(
            level,
            "unexpected_pause",
            {
                errorMessage,
                totalDurationMs: hasValidDuration
                    ? Math.round(normalizedDuration * 1000)
                    : null,
                playedFraction: Math.round(playedFraction * 1000) / 1000,
            },
            audio,
        );
    }

    private maybeLogUnexpectedEnd(
        audio: HTMLAudioElement,
        expectedSeconds: number,
        errorMessage: string,
    ) {
        if (!Number.isFinite(expectedSeconds) || expectedSeconds <= 0) {
            return;
        }

        const actualDurationMs = Math.round(
            this.getAudioCurrentTime(audio) * 1000,
        );
        const expectedDurationMs = Math.round(expectedSeconds * 1000);
        if (
            expectedDurationMs - actualDurationMs <=
            UNEXPECTED_END_TOLERANCE_MS
        ) {
            return;
        }

        // AIDEV-NOTE: Severity classification for unexpected end events.
        // <95% played = genuine interruption (error), ≥95% = near-complete (warn).
        const playedFraction =
            expectedDurationMs > 0 ? actualDurationMs / expectedDurationMs : 0;
        const level = playedFraction < 0.95 ? "error" : "warn";

        this.logBreak(
            level,
            "unexpected_end",
            {
                errorMessage,
                expectedDurationMs,
                actualDurationMs,
                playedFraction: Math.round(playedFraction * 1000) / 1000,
            },
            audio,
        );
    }

    private mapHlsBreakType(details?: string): MeditationPlaybackBreakType {
        const normalizedDetails = details ?? "";
        if (normalizedDetails.startsWith("manifest")) {
            return "hls_manifest_error";
        }
        if (normalizedDetails.startsWith("frag")) {
            return "hls_fragment_error";
        }
        if (normalizedDetails.startsWith("buffer")) {
            return "hls_buffer_error";
        }
        return "hls_fatal_other";
    }

    // AIDEV-NOTE: EVO-1813 — only arm the stall indicator while the user is
    // actually listening to the main element. Startup buffering already has
    // its own loading affordances, and intro-phase events on the (paused)
    // main element must not trigger the indicator.
    private handleMainStallSignal() {
        const state = this.getState();
        if (
            !state.isPlaying ||
            this.getActiveAudioElement() !== this.mainAudio
        ) {
            return;
        }
        this.stallDetector.notifyWaiting();
    }

    private resolveActiveStall(audio: HTMLAudioElement) {
        if (this._stallStartedAtMs === null) {
            return;
        }

        const now = Date.now();
        const stallDurationMs = now - this._stallStartedAtMs;
        this._stallStartedAtMs = null;
        this._lastObservedStallEndedAtMs = now;

        if (stallDurationMs < STALL_LOG_MIN_MS) {
            meditationVerboseDebugLog(
                "[AudioController] ignoring micro-stall",
                {
                    stallDurationMs,
                    ...this.getAudioDebugSnapshot(audio),
                },
            );
            return;
        }

        this.logBreak(
            "warn",
            "stall",
            {
                stallDurationMs,
            },
            audio,
        );
    }

    private shouldSuppressHlsJsBreak(
        errorData: HlsErrorData,
        breakType: MeditationPlaybackBreakType,
    ) {
        if (errorData.fatal || breakType !== "hls_buffer_error") {
            return false;
        }

        const timeSincePlaybackStartMs = this.getTimeSincePlaybackStartMs();
        if (timeSincePlaybackStartMs === null) {
            meditationVerboseDebugLog(
                "[AudioController] suppressing pre-play hls.js buffer error",
                {
                    details: errorData.details ?? null,
                    type: errorData.type ?? null,
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                },
            );
            return true;
        }

        if (timeSincePlaybackStartMs < HLS_JS_EARLY_BREAK_SUPPRESSION_MS) {
            meditationVerboseDebugLog(
                "[AudioController] suppressing early hls.js buffer error",
                {
                    details: errorData.details ?? null,
                    type: errorData.type ?? null,
                    timeSincePlaybackStartMs,
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                },
            );
            return true;
        }

        if (
            this._lastObservedStallEndedAtMs !== null &&
            Date.now() - this._lastObservedStallEndedAtMs <
                HLS_JS_BREAK_DEDUP_AFTER_STALL_MS
        ) {
            meditationVerboseDebugLog(
                "[AudioController] suppressing duplicate hls.js buffer error after stall",
                {
                    details: errorData.details ?? null,
                    type: errorData.type ?? null,
                    timeSincePlaybackStartMs,
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                },
            );
            return true;
        }

        return false;
    }

    private getHlsErrorPayload(data: HlsErrorData) {
        return {
            hlsErrorType: data.type ?? undefined,
            hlsErrorDetails: data.details ?? undefined,
            hlsResponseCode: data.response?.code ?? undefined,
            hlsUrl: data.url?.slice(0, 140),
            hlsFatal: data.fatal,
        };
    }

    /**
     * Set up audio element event listeners.
     * These dispatch actions to update state when audio events occur.
     */
    private setupEventListeners() {
        // Intro audio events
        // AIDEV-NOTE: Use 'playing' event instead of 'play' - 'playing' fires when
        // actual playback begins (after buffering), while 'play' fires immediately
        // when play() is called. This ensures breathing animation shows during buffering.
        this.introAudio.addEventListener("playing", () => {
            this.maybeLogFirstStablePlaying(this.introAudio);
            this.dispatch({ type: "INTRO_PLAYBACK_STARTED" });
        });

        this.introAudio.addEventListener("timeupdate", () => {
            const state = this.getState();
            if (state.phase === "intro") {
                this.dispatch({
                    type: "TIME_UPDATE",
                    time: this.introAudio.currentTime,
                });
            }
        });

        this.introAudio.addEventListener("ended", () => {
            this.maybeLogUnexpectedEnd(
                this.introAudio,
                this.getState().introDuration,
                "Intro audio ended before the expected duration.",
            );
            this.dispatch({ type: "INTRO_ENDED" });
            this.startGapTimer();
        });

        this.introAudio.addEventListener("pause", () => {
            const state = this.getState();
            if (state.phase === "intro" && state.isPlaying) {
                this.maybeLogUnexpectedPause(
                    this.introAudio,
                    "Intro audio paused unexpectedly during playback.",
                );
                this.dispatch({ type: "PAUSE" });
            }
        });

        this.introAudio.addEventListener("error", () => {
            const state = this.getState();
            if (state.phase !== "intro" || !state.isPlaying) {
                return;
            }

            this.resolveActiveStall(this.introAudio);

            this.logMediaError(
                this.introAudio,
                "error",
                "Intro audio element emitted a media error during playback.",
            );
            this.failPlayback("Failed to load audio. Please try again.");
        });

        this.setupMainEventListeners();
    }

    /**
     * Attach event listeners to the main audio element.
     * AIDEV-NOTE: Extracted so it can be called again after replaceMainAudioElement().
     */
    private setupMainEventListeners() {
        // AIDEV-NOTE: Use 'playing' event instead of 'play' - 'playing' fires when
        // actual playback begins (after buffering), while 'play' fires immediately
        // when play() is called. This ensures breathing animation shows during buffering.
        this.mainAudio.addEventListener("playing", () => {
            this.resolveActiveStall(this.mainAudio);
            this.stallDetector.notifyPlaying();
            this.maybeLogFirstStablePlaying(this.mainAudio);
            this.dispatch({ type: "MAIN_PLAYBACK_STARTED" });
            this.emitHlsBufferingStatus();
        });

        // AIDEV-NOTE: EVO-1813 — single attach point for the UI stall indicator.
        // Covers MSE, native-HLS, hls.js, and direct-URL paths because they all
        // play through this element (re-attached after replaceMainAudioElement).
        this.mainAudio.addEventListener("waiting", () => {
            this.handleMainStallSignal();
        });
        this.mainAudio.addEventListener("stalled", () => {
            this.handleMainStallSignal();
        });

        this.mainAudio.addEventListener("timeupdate", () => {
            this.stallDetector.notifyTimeUpdate(this.mainAudio.currentTime);
            const state = this.getState();
            if (state.phase === "main") {
                const normalizedMainTime = this.normalizeFallbackMainTime(
                    this.mainAudio.currentTime,
                );
                this.maybeEmitHlsAudibleStart(
                    this.mainAudio.currentTime,
                    normalizedMainTime,
                );
                // AIDEV-NOTE: In fallback mode (URL playback), there's no intro/gap,
                // so currentTime is just the audio element's time directly.
                const offset =
                    this._fallbackMode && state.introDuration === 0
                        ? 0
                        : state.introDuration + GAP_DURATION;
                const combinedTime = offset + normalizedMainTime;
                this.dispatch({ type: "TIME_UPDATE", time: combinedTime });
            }
            this.emitHlsBufferingStatus();
        });

        this.mainAudio.addEventListener("ended", () => {
            this.resolveActiveStall(this.mainAudio);
            // AIDEV-NOTE: Drop pending/active UI stall silently — MAIN_ENDED
            // clears isStalled in the reducer.
            this.stallDetector.reset();
            this.maybeLogUnexpectedEnd(
                this.mainAudio,
                this.getState().mainDuration,
                "Main audio ended before the expected duration.",
            );
            // AIDEV-NOTE: Instrument event ordering for the stuck-spinner
            // bug: if MAIN_ENDED fires while isGenerating is still true,
            // the UI spinner is already orphaned (GENERATION_COMPLETE
            // hasn't arrived yet). See 2026-04-14 meditation handoff.
            const preDispatchState = this.getState();
            logger
                .withContext({
                    event: "meditation.main_ended.dispatch",
                    phase: preDispatchState.phase,
                    isGenerating: preDispatchState.isGenerating,
                    mainDuration: preDispatchState.mainDuration,
                    currentTime: this.mainAudio.currentTime,
                    audioDuration: Number.isFinite(this.mainAudio.duration)
                        ? this.mainAudio.duration
                        : null,
                    orphanedGeneratingFlag: preDispatchState.isGenerating,
                })
                .info("meditation.main_ended.dispatch");
            this.dispatch({ type: "MAIN_ENDED" });
            this.emitHlsBufferingStatus();
        });

        this.mainAudio.addEventListener("pause", () => {
            const state = this.getState();
            if (state.phase === "main" && state.isPlaying) {
                this.maybeLogUnexpectedPause(
                    this.mainAudio,
                    "Main audio paused unexpectedly during playback.",
                );
                this.dispatch({ type: "PAUSE" });
            }
        });

        this.mainAudio.addEventListener("error", () => {
            const state = this.getState();
            if (state.phase !== "main" || !state.isPlaying) {
                return;
            }

            this.resolveActiveStall(this.mainAudio);

            this.logMediaError(
                this.mainAudio,
                "error",
                "Main audio element emitted a media error during playback.",
            );
            this.failPlayback("Failed to load audio. Please try again.");
        });

        // AIDEV-NOTE: Browser-native duration discovery. Three sub-modes of
        // fallback each have a different "duration is final" signal:
        //   - Native HLS (Safari): <audio>.duration grows as manifest is
        //     re-polled; debounce until stable for NATIVE_HLS_DURATION_STABLE_MS.
        //   - hls.js: authoritative signal comes via Hls.Events.LEVEL_LOADED
        //     with details.live === false (registered in startHlsJsPlayback).
        //     Skip the durationchange-based path entirely.
        //   - Direct-URL MP3 fallback: dispatch immediately on first valid
        //     duration (happens once when metadata is loaded).
        // All three share a once-per-session guard so we never re-dispatch.
        this.mainAudio.addEventListener("durationchange", () => {
            if (!this._fallbackMode) return;
            if (this._nativeHlsPlayback) {
                this.scheduleNativeHlsDurationDiscovery();
            } else if (!this._hlsJsPlayback) {
                this.dispatchDurationDiscoveredOnce(this.mainAudio.duration);
            }
            this.emitHlsBufferingStatus();
        });

        this.mainAudio.addEventListener("progress", () => {
            this.emitHlsBufferingStatus();
        });

        this.mainAudio.addEventListener("loadedmetadata", () => {
            this.emitHlsBufferingStatus();
        });
    }

    private resetDurationDiscovery() {
        this._durationDiscoveredDispatched = false;
        if (this._nativeHlsDurationStableTimeout !== null) {
            clearTimeout(this._nativeHlsDurationStableTimeout);
            this._nativeHlsDurationStableTimeout = null;
        }
    }

    private dispatchDurationDiscoveredOnce(durationSeconds: number) {
        if (this._durationDiscoveredDispatched) return;
        const duration = this.normalizeFallbackMainDuration(durationSeconds);
        if (!Number.isFinite(duration) || duration <= 0) return;
        this._durationDiscoveredDispatched = true;
        this.dispatch({ type: "DURATION_DISCOVERED", duration });
    }

    private scheduleNativeHlsDurationDiscovery() {
        const candidate = this.normalizeFallbackMainDuration(
            this.mainAudio.duration,
        );
        if (!Number.isFinite(candidate) || candidate <= 0) return;
        if (this._nativeHlsDurationStableTimeout !== null) {
            clearTimeout(this._nativeHlsDurationStableTimeout);
        }
        this._nativeHlsDurationStableTimeout = setTimeout(() => {
            this._nativeHlsDurationStableTimeout = null;
            const current = this.normalizeFallbackMainDuration(
                this.mainAudio.duration,
            );
            // AIDEV-NOTE: If <audio>.duration has grown in the last
            // NATIVE_HLS_DURATION_STABLE_MS window, the manifest is still
            // being extended — bail and let the next durationchange
            // re-schedule. When the value is stable, the manifest is (in
            // practice) finalized and the duration is accurate.
            if (Math.abs(current - candidate) > 0.25) return;
            this.dispatchDurationDiscoveredOnce(this.mainAudio.duration);
        }, NATIVE_HLS_DURATION_STABLE_MS);
    }

    private resetFallbackPlaybackProfile() {
        this.resetDurationDiscovery();
        this._nativeHlsPlayback = false;
        this._hlsJsPlayback = false;
        this._nativeHlsStartOffsetSeconds = null;
        this._nativeHlsAudibleStartThresholdSeconds = 0;
        this._nativeHlsAudibleStartLogged = false;
        this._stallStartedAtMs = null;
        this._lastObservedStallEndedAtMs = null;
        this._awaitingUserGestureRecovery = false;
    }

    private destroyHlsJsInstance() {
        if (!this._hlsJsInstance) {
            return;
        }

        try {
            this._hlsJsInstance.destroy();
        } catch (error) {
            console.warn(
                "[AudioController] failed to destroy hls.js instance",
                {
                    error,
                },
            );
        } finally {
            this._hlsJsInstance = null;
        }
    }

    private maybeCaptureNativeHlsStartOffset() {
        if (
            !this._nativeHlsPlayback ||
            this._nativeHlsStartOffsetSeconds !== null
        ) {
            return;
        }

        const currentTime = this.mainAudio.currentTime;
        if (!Number.isFinite(currentTime) || currentTime <= 0) {
            return;
        }

        this._nativeHlsStartOffsetSeconds = currentTime;
        meditationDebugLog(
            "[AudioController] captured native HLS start offset",
            {
                offsetSeconds: currentTime,
            },
        );
    }

    private normalizeFallbackMainTime(timeSeconds: number) {
        if (!this._nativeHlsPlayback) {
            return timeSeconds;
        }

        this.maybeCaptureNativeHlsStartOffset();
        return Math.max(
            0,
            timeSeconds - (this._nativeHlsStartOffsetSeconds ?? 0),
        );
    }

    private maybeEmitHlsAudibleStart(
        rawTimeSeconds: number,
        normalizedTimeSeconds: number,
    ) {
        const activeHlsPlayback =
            this._nativeHlsPlayback || this._hlsJsPlayback;
        const thresholdTimeSeconds = this._nativeHlsPlayback
            ? rawTimeSeconds
            : normalizedTimeSeconds;
        if (
            !activeHlsPlayback ||
            this._nativeHlsAudibleStartLogged ||
            this._nativeHlsAudibleStartThresholdSeconds <= 0 ||
            thresholdTimeSeconds <=
                this._nativeHlsAudibleStartThresholdSeconds +
                    NATIVE_HLS_AUDIBLE_PROGRESS_EPSILON_SECONDS
        ) {
            return;
        }

        this._nativeHlsAudibleStartLogged = true;
        // AIDEV-NOTE: Safari can start fallback HLS at a non-zero live-edge time.
        // Gate the fullscreen transition off the raw media clock, not the
        // normalized UI clock, so breathing ends when audio is truly audible.
        const detail = {
            mode: this._nativeHlsPlayback ? "native" : "hls.js",
            rawTimeSeconds,
            normalizedTimeSeconds,
            audibleStartThresholdSeconds:
                this._nativeHlsAudibleStartThresholdSeconds,
            audibleProgressEpsilonSeconds:
                NATIVE_HLS_AUDIBLE_PROGRESS_EPSILON_SECONDS,
            capturedStartOffsetSeconds: this._nativeHlsStartOffsetSeconds ?? 0,
            audioCurrentTime: this.mainAudio.currentTime,
            audioInstanceId: this.mainAudio.dataset.audioInstanceId ?? null,
            currentSrc: this.mainAudio.currentSrc.slice(0, 160),
        };

        meditationDebugLog(
            "[AudioController] native HLS reached audible threshold",
            detail,
        );
        if (
            typeof window.dispatchEvent === "function" &&
            typeof CustomEvent !== "undefined"
        ) {
            window.dispatchEvent(
                new CustomEvent("meditation:hls-audible-start", { detail }),
            );
        }
    }

    private normalizeFallbackMainDuration(durationSeconds: number) {
        if (!this._nativeHlsPlayback) {
            return durationSeconds;
        }

        this.maybeCaptureNativeHlsStartOffset();
        return Math.max(
            0,
            durationSeconds - (this._nativeHlsStartOffsetSeconds ?? 0),
        );
    }

    private getBufferedEndSeconds() {
        const { buffered } = this.mainAudio;
        if (!buffered || buffered.length === 0) {
            return 0;
        }

        try {
            return buffered.end(buffered.length - 1);
        } catch {
            return 0;
        }
    }

    private emitHlsBufferingStatus() {
        if (
            (!this._nativeHlsPlayback && !this._hlsJsPlayback) ||
            typeof window.dispatchEvent !== "function" ||
            typeof CustomEvent === "undefined"
        ) {
            return;
        }

        const normalizedDuration = this.normalizeFallbackMainDuration(
            this.mainAudio.duration,
        );
        const bufferedEndSeconds = this.normalizeFallbackMainTime(
            this.getBufferedEndSeconds(),
        );
        const hasKnownDuration =
            Number.isFinite(normalizedDuration) && normalizedDuration > 0;
        const isFullyBuffered =
            hasKnownDuration &&
            bufferedEndSeconds >=
                normalizedDuration - HLS_FULL_BUFFER_EPSILON_SECONDS;

        // AIDEV-NOTE: The player hides the final duration until the client has
        // actually buffered the full HLS run; manifest duration alone is too optimistic.
        window.dispatchEvent(
            new CustomEvent("meditation:hls-buffering-status", {
                detail: {
                    mode: this._nativeHlsPlayback ? "native" : "hls.js",
                    isFullyBuffered,
                    bufferedEndSeconds,
                    durationSeconds: hasKnownDuration
                        ? normalizedDuration
                        : null,
                    audioCurrentTime: this.mainAudio.currentTime,
                    audioInstanceId:
                        this.mainAudio.dataset.audioInstanceId ?? null,
                },
            }),
        );
    }

    /**
     * Start the gap timer between intro and main audio
     */
    private startGapTimer() {
        this.gapTimeoutId = window.setTimeout(() => {
            this.dispatch({ type: "GAP_ENDED" });
            this.tryPlayMain();
        }, GAP_DURATION * 1000);
    }

    // --- Public Methods ---

    /**
     * Check if controller is in fallback mode (no MediaSource support)
     */
    get fallbackMode(): boolean {
        return this._fallbackMode;
    }

    // AIDEV-NOTE: Safari should prefer native HLS even when MediaSource exists,
    // so the hook asks the controller whether to bypass the custom MSE path.
    shouldPreferHlsPlaybackPath(): boolean {
        return supportsNativeHlsPlayback(this.mainAudio);
    }

    /**
     * Store the signed audio URL for fallback playback.
     * AIDEV-NOTE: Called when the complete event provides an audioUrl.
     * Used as fallback when MediaSource can't decode cached format (e.g., MP3 vs fMP4/AAC).
     */
    setAudioUrl(url: string | undefined) {
        this._audioUrl = url ?? null;
    }

    /**
     * Whether any main audio chunks have been received.
     * AIDEV-NOTE: When the server detects a cached MP3 file, it skips chunk streaming
     * and only sends the audioUrl. The client uses this to decide between MediaSource
     * and URL-based playback.
     */
    get receivedMainChunks(): boolean {
        return this.mainTotalBytes > 0;
    }

    getCurrentMainTime(): number {
        return this.mainAudio.currentTime;
    }

    /**
     * Suppress automatic playback start.
     * AIDEV-NOTE: Chunks continue to buffer into MediaSource, but
     * maybeStartIntroPlayback/maybeStartMainPlayback won't call play().
     */
    suppressPlayback() {
        this._playbackSuppressed = true;
    }

    /**
     * Clear playback suppression and start playing whatever's been buffered.
     * AIDEV-NOTE: Called from start() after audio unlock, in user gesture context.
     */
    unsuppressPlayback() {
        this._playbackSuppressed = false;
        this.maybeStartIntroPlayback();
        this.maybeStartMainPlayback();
    }

    private getAudioDebugSnapshot(audio: HTMLAudioElement) {
        let bufferedEnd: number | null = null;
        let seekableEnd: number | null = null;
        let playedEnd: number | null = null;
        try {
            if (audio.buffered.length > 0) {
                bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
            }
        } catch {
            bufferedEnd = null;
        }

        try {
            if (audio.seekable.length > 0) {
                seekableEnd = audio.seekable.end(audio.seekable.length - 1);
            }
        } catch {
            seekableEnd = null;
        }

        try {
            if (audio.played.length > 0) {
                playedEnd = audio.played.end(audio.played.length - 1);
            }
        } catch {
            playedEnd = null;
        }

        const mediaError = audio.error;
        const debugAudio = audio as HTMLAudioElement & {
            webkitAudioDecodedByteCount?: number;
        };

        return {
            readyState: audio.readyState,
            networkState: audio.networkState,
            currentTime: audio.currentTime,
            duration: Number.isFinite(audio.duration) ? audio.duration : null,
            bufferedEnd,
            seekableEnd,
            playedEnd,
            paused: audio.paused,
            ended: audio.ended,
            currentSrc: audio.currentSrc.slice(0, 160),
            errorCode: mediaError?.code ?? null,
            errorMessage: mediaError?.message ?? null,
            webkitAudioDecodedByteCount:
                debugAudio.webkitAudioDecodedByteCount ?? null,
        };
    }

    private getMainAudioInstanceLabel() {
        return `main-audio-${this._mainAudioInstanceId}`;
    }

    /**
     * AIDEV-NOTE: Safari failures have been occurring after playback starts,
     * so emit short-lived periodic snapshots rather than only one-shot events.
     */
    private startUrlPlaybackStateMonitor(context: string) {
        const startedAt = Date.now();
        let tickCount = 0;
        const maxTicks = 20;

        const intervalId = window.setInterval(() => {
            tickCount += 1;
            meditationVerboseDebugLog(`[AudioController] ${context}:monitor`, {
                tickCount,
                elapsedMs: Date.now() - startedAt,
                ...this.getAudioDebugSnapshot(this.mainAudio),
            });

            if (
                tickCount >= maxTicks ||
                this.mainAudio.ended ||
                this.mainAudio.error
            ) {
                window.clearInterval(intervalId);
            }
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }

    /**
     * AIDEV-NOTE: One-shot URL playback logs let us see whether signed URLs are
     * blocking before metadata, before canplay, or before actual playback.
     */
    private attachUrlPlaybackDebugLogging(context: string) {
        const startedAt = Date.now();
        const events = [
            "loadstart",
            "progress",
            "durationchange",
            "loadedmetadata",
            "loadeddata",
            "canplay",
            "canplaythrough",
            "playing",
            "waiting",
            "stalled",
            "suspend",
            "abort",
            "emptied",
            "error",
        ] as const;

        for (const eventName of events) {
            this.mainAudio.addEventListener(
                eventName,
                () => {
                    meditationVerboseDebugLog(
                        `[AudioController] ${context}:${eventName}`,
                        {
                            elapsedMs: Date.now() - startedAt,
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                },
                { once: true },
            );
        }
    }

    /**
     * Initialize for new streaming session.
     * Resets all state for fresh playback.
     * @returns Object with success status and fallbackMode flag
     */
    initializeMediaSource(): { success: boolean; fallbackMode: boolean } {
        // Reset fallback mode
        this._fallbackMode = false;

        const mediaSourceAvailable = typeof MediaSource !== "undefined";
        const mimeSupported = mediaSourceAvailable
            ? isMediaSourceSupported()
            : false;

        // AIDEV-NOTE: Force fallback mode if requested via URL param (for testing)
        // or if MediaSource is not supported (iOS Safari < 17.1).
        // In fallback mode, we use direct URL streaming instead of MediaSource chunks.
        if (this._forceFallback) {
            meditationDebugLog(
                "[AudioController] initializeMediaSource: forcing fallback mode (forceFallback=true)",
                {
                    mediaSourceAvailable,
                    mimeSupported,
                },
            );
            this._fallbackMode = true;
            return { success: true, fallbackMode: true };
        }

        // AIDEV-NOTE: Check MediaSource support before attempting to use it.
        // iOS Safari < 17.1 doesn't have MediaSource API at all.
        // In fallback mode, we wait for the complete audioUrl instead of streaming chunks.
        if (!isMediaSourceSupported()) {
            meditationDebugLog(
                "[AudioController] initializeMediaSource: MediaSource not supported, using fallback",
                {
                    mediaSourceAvailable,
                    mimeSupported,
                },
            );
            this._fallbackMode = true;
            // Still return success - we'll use URL-based playback as fallback
            return { success: true, fallbackMode: true };
        }

        meditationDebugLog(
            "[AudioController] initializeMediaSource: using MediaSource streaming mode",
            {
                mediaSourceAvailable,
                mimeSupported,
            },
        );

        // Cleanup any existing MediaSource
        this.cleanupIntroMediaSource();
        this.cleanupMainMediaSource();

        // Reset intro state
        this.introPendingChunks = [];
        this.introTotalBytes = 0;
        this.introStreamEnded = false;
        this.hasNotifiedIntroReady = false;
        this.introPlaybackStarted = false;

        // Reset main state
        this.mainPendingChunks = [];
        this.mainTotalBytes = 0;
        this.mainStreamEnded = false;
        this.hasNotifiedMainReady = false;
        this.mainPlaybackStarted = false;

        // Initialize MediaSource for intro
        this.initializeIntroMediaSource();

        // Initialize MediaSource for main
        this.initializeMainMediaSource();

        return { success: true, fallbackMode: false };
    }

    /**
     * Initialize MediaSource for intro audio streaming
     */
    private initializeIntroMediaSource() {
        // AIDEV-NOTE: isMediaSourceSupported() already checked in initializeMediaSource(),
        // but we keep this guard for safety in case method is called directly.
        if (!isMediaSourceSupported()) {
            console.error(
                "[AudioController] MediaSource not supported for fMP4/AAC",
            );
            return;
        }

        this.introMediaSource = new MediaSource();
        this.introAudio.src = URL.createObjectURL(this.introMediaSource);

        this.introMediaSource.addEventListener("sourceopen", () => {
            if (!this.introMediaSource) return;

            try {
                this.introSourceBuffer =
                    this.introMediaSource.addSourceBuffer(FMP4_AAC_MIME_TYPE);

                this.introSourceBuffer.addEventListener("updateend", () => {
                    this.processIntroPendingChunks();
                    this.maybeStartIntroPlayback();
                });

                // Process any chunks that arrived before sourceopen
                this.processIntroPendingChunks();
            } catch (err) {
                console.error(
                    "[AudioController] Failed to create intro SourceBuffer:",
                    err,
                );
            }
        });
    }

    /**
     * Initialize MediaSource for main audio streaming
     */
    private initializeMainMediaSource() {
        // AIDEV-NOTE: isMediaSourceSupported() already checked in initializeMediaSource(),
        // but we keep this guard for safety in case method is called directly.
        if (!isMediaSourceSupported()) {
            console.error(
                "[AudioController] MediaSource not supported for fMP4/AAC",
            );
            return;
        }

        this.mainMediaSource = new MediaSource();
        this.mainAudio.src = URL.createObjectURL(this.mainMediaSource);

        this.mainMediaSource.addEventListener("sourceopen", () => {
            if (!this.mainMediaSource) return;

            try {
                this.mainSourceBuffer =
                    this.mainMediaSource.addSourceBuffer(FMP4_AAC_MIME_TYPE);

                this.mainSourceBuffer.addEventListener("updateend", () => {
                    this.processMainPendingChunks();
                    this.maybeStartMainPlayback();
                });

                // Process any chunks that arrived before sourceopen
                this.processMainPendingChunks();
            } catch (err) {
                console.error(
                    "[AudioController] Failed to create main SourceBuffer:",
                    err,
                );
            }
        });
    }

    /**
     * Append an intro audio chunk - streams to MediaSource
     */
    appendIntroChunk(chunk: Uint8Array) {
        this.introTotalBytes += chunk.length;
        this.introPendingChunks.push(chunk);
        this.processIntroPendingChunks();
        this.maybeNotifyIntroReady();
    }

    /**
     * Finalize intro stream - signal end of stream to MediaSource
     */
    finalizeIntroStream() {
        this.introStreamEnded = true;
        this.processIntroPendingChunks();

        // Force start playback if not started yet
        if (!this.introPlaybackStarted) {
            this.maybeStartIntroPlayback();
        }
    }

    /**
     * Append a main audio chunk - streams to MediaSource
     */
    appendChunk(chunk: Uint8Array) {
        this.mainTotalBytes += chunk.length;
        this.mainPendingChunks.push(chunk);
        this.processMainPendingChunks();
        this.maybeNotifyMainReady();
    }

    /**
     * Finalize the main audio stream - signal end of stream to MediaSource
     */
    finalizeStream() {
        this.mainStreamEnded = true;
        this.processMainPendingChunks();

        // Force start playback if not started yet
        const state = this.getState();
        const noIntroReceived = this.introTotalBytes === 0;
        // AIDEV-NOTE: Also handle idle phase when no intro received (input streaming)
        if (
            !this.mainPlaybackStarted &&
            (state.phase === "gap" ||
                (state.phase === "idle" && noIntroReceived))
        ) {
            this.maybeStartMainPlayback();
        }
    }

    /**
     * Play intro audio (for manual resume after pause)
     */
    playIntro() {
        if (!this.introMediaSource) {
            return;
        }

        this.introAudio.play().catch((err) => {
            // AIDEV-NOTE: Log error name/message + audio state to distinguish
            // NotAllowedError (autoplay) vs AbortError (interrupted) vs NotSupportedError (format)
            console.error("[AudioController] playIntro error:", {
                errorName: err?.name,
                errorMessage: err?.message,
                readyState: this.introAudio.readyState,
                networkState: this.introAudio.networkState,
                paused: this.introAudio.paused,
                currentSrc: this.introAudio.currentSrc,
                mediaSourceState: this.introMediaSource?.readyState ?? "none",
            });
            this.logPlayRejected(
                this.introAudio,
                err,
                "warn",
                "playIntro() was rejected by the browser.",
            );
        });
    }

    /**
     * Try to play main audio.
     * Called after gap timer or when generation completes.
     */
    tryPlayMain() {
        if (!this.mainMediaSource) {
            return;
        }

        // Start playback if we have buffered data
        this.maybeStartMainPlayback();
    }

    /**
     * Play audio from a URL (fallback mode for browsers without MediaSource).
     * AIDEV-NOTE: This is called when the complete event includes an audioUrl.
     * The audio plays via progressive download from the cached file.
     */
    playFromUrl(url: string) {
        // AIDEV-NOTE: Set fallback mode so play()/pause() use the simple mainAudio-only paths
        this._fallbackMode = true;
        this.resetFallbackPlaybackProfile();

        // Use mainAudio element for URL-based playback
        this.mainAudio.src = url;
        this.mainAudio.preload = "metadata";
        this.attachUrlPlaybackDebugLogging("playFromUrl");

        // Dispatch to update state - skip intro phase in fallback mode
        this.dispatch({ type: "MAIN_PLAYBACK_STARTED" });

        // AIDEV-NOTE: Prefer loadedmetadata over canplay so signed URL playback
        // can request actual playback as soon as the browser understands the file.
        const onLoadedMetadata = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            this.mainAudio.play().catch((err) => {
                // AIDEV-NOTE: NotAllowedError = expired user gesture on iOS.
                // Audio is loaded and ready — just need a fresh tap.
                if (err?.name === "NotAllowedError") {
                    this._awaitingUserGestureRecovery = true;
                    meditationDebugWarn(
                        "[AudioController] playFromUrl: NotAllowedError — needs fresh user gesture",
                        {
                            errorName: err?.name,
                            readyState: this.mainAudio.readyState,
                            networkState: this.mainAudio.networkState,
                        },
                    );
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        "warn",
                        "playFromUrl() rejected by autoplay policy — awaiting user gesture.",
                    );
                    this.dispatch({ type: "NEEDS_USER_GESTURE" });
                    return;
                }
                // AIDEV-NOTE: Log error details + audio state for iOS Safari diagnosis
                console.error("[AudioController] playFromUrl error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                    currentSrc: this.mainAudio.currentSrc,
                    mediaSourceState:
                        this.mainMediaSource?.readyState ?? "none",
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "playFromUrl() was rejected by the browser.",
                );
                this.failPlayback("Failed to play audio. Please try again.");
            });
        };
        this.mainAudio.addEventListener("loadedmetadata", onLoadedMetadata);
    }

    // AIDEV-NOTE: Resume playback after NotAllowedError with a fresh user gesture.
    // The audio element still has its src and buffered data — just call play().
    resumeWithUserGesture() {
        this._awaitingUserGestureRecovery = false;
        meditationDebugLog("[AudioController] resumeWithUserGesture: called", {
            readyState: this.mainAudio.readyState,
            networkState: this.mainAudio.networkState,
            currentSrc: this.mainAudio.currentSrc?.substring(0, 100),
            paused: this.mainAudio.paused,
        });

        this.mainAudio
            .play()
            .then(() => {
                meditationDebugLog(
                    "[AudioController] resumeWithUserGesture: play() succeeded",
                );
                this.dispatch({ type: "MAIN_PLAYBACK_STARTED" });
                this.attachHlsStallDetection();
            })
            .catch((err) => {
                console.error(
                    "[AudioController] resumeWithUserGesture play error",
                    {
                        errorName: err?.name,
                        playErrorMessage: err?.message,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "resumeWithUserGesture() play() was rejected.",
                );
                this.failPlayback("Failed to play audio. Please try again.");
            });
    }

    // AIDEV-NOTE: HLS startup retry state — reset on each startHlsPlayback call.
    private _hlsStartupRetryCount = 0;
    private _hlsStartupRetryCleanup: (() => void) | null = null;
    // AIDEV-NOTE: Timeout extension counter — allows one extension when partial buffer detected.
    private _timeoutExtensionCount = 0;

    /**
     * Start native HLS playback from a bootstrap playlist URL.
     * AIDEV-NOTE: Safari fallback now prefers HLS over chunked direct audio for
     * fresh generation because native HLS handles container startup more reliably.
     *
     * AIDEV-NOTE: Includes startup retry — if Safari errors before the first
     * `playing` event (e.g. transient network drop), we re-assign the same src
     * up to 2 times with fixed delays before giving up.
     */
    startHlsPlayback(
        url: string,
        audibleStartThresholdSeconds = 0,
        options?: {
            forceHlsJs?: boolean;
        },
    ) {
        this._fallbackMode = true;
        this.resetFallbackPlaybackProfile();
        this._nativeHlsAudibleStartThresholdSeconds =
            audibleStartThresholdSeconds;

        // Clean up any pending retry from a prior call
        this._hlsStartupRetryCleanup?.();
        this._hlsStartupRetryCleanup = null;
        this._timeoutExtensionCount = 0;

        this.destroyHlsJsInstance();

        const forceHlsJs = options?.forceHlsJs ?? false;
        if (forceHlsJs && Hls.isSupported()) {
            this._hlsJsPlayback = true;
            this.startHlsJsPlayback(url, audibleStartThresholdSeconds);
            return;
        }

        if (!supportsNativeHlsPlayback(this.mainAudio) && Hls.isSupported()) {
            this._hlsJsPlayback = true;
            this.startHlsJsPlayback(url, audibleStartThresholdSeconds);
            return;
        }

        this._nativeHlsPlayback = true;
        this._hlsStartupRetryCount = 0;
        this._timeoutExtensionCount = 0;
        this.attemptHlsStartup(url, audibleStartThresholdSeconds);
    }

    private startHlsJsPlayback(
        url: string,
        audibleStartThresholdSeconds: number,
    ) {
        const hls = new Hls({
            // AIDEV-NOTE: Meditations are short VOD-like runs. Once segments are
            // available, let hls.js buffer the full remaining file instead of
            // capping forward buffer at 30s.
            maxBufferLength: HLS_JS_FORWARD_BUFFER_TARGET_SECONDS,
            maxMaxBufferLength: HLS_JS_FORWARD_BUFFER_TARGET_SECONDS,
            // AIDEV-NOTE: hls.js treats live/EVENT playlists as live-edge by
            // default. Meditation playback must always start from seg-1.
            startPosition: HLS_JS_MEDITATION_START_POSITION_SECONDS,
            debug: isMeditationVerboseDebugEnabled(),
        });
        this._hlsJsInstance = hls;
        this.mainAudio.preload = "auto";
        this.attachUrlPlaybackDebugLogging("startHlsJsPlayback");
        const stopMonitor =
            this.startUrlPlaybackStateMonitor("startHlsJsPlayback");
        const STARTUP_TIMEOUT = getStartupTimeoutMs();
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let playbackStarted = false;
        let playRequested = false;
        let warmupTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let warmupExpired = false;

        meditationDebugLog(
            "[AudioController] startHlsJsPlayback: attaching source",
            {
                audioInstanceId: this.mainAudio.dataset.audioInstanceId,
                url: url.substring(0, 140),
                audibleStartThresholdSeconds,
            },
        );

        const cleanupStartup = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            this.mainAudio.removeEventListener(
                "loadeddata",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("canplay", maybeStartPlayback);
            this.mainAudio.removeEventListener(
                "canplaythrough",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("progress", maybeStartPlayback);
            this.mainAudio.removeEventListener(
                "durationchange",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("playing", onPlaying);
            this.mainAudio.removeEventListener("error", onMediaError);
            hls.off(Hls.Events.ERROR, onHlsError);
            // AIDEV-NOTE: LEVEL_LOADED/LEVEL_UPDATED deliberately NOT
            // unhooked here. Playback starts while the playlist is still
            // live (generation in flight), so the finalized ENDLIST refresh
            // arrives AFTER startup cleanup — unhooking meant hls.js
            // sessions never dispatched DURATION_DISCOVERED and the player
            // showed "…" as the total forever. hls.destroy() removes the
            // listeners on teardown, and dispatchDurationDiscoveredOnce is
            // idempotent.
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (warmupTimeoutId !== null) {
                clearTimeout(warmupTimeoutId);
                warmupTimeoutId = null;
            }
            stopMonitor();
        };

        const onPlaying = () => {
            playbackStarted = true;
            cleanupStartup();
            this.attachHlsStallDetection();
        };

        const maybeStartPlayback = () => {
            if (playRequested) {
                return;
            }

            const state = this.getState();
            const bufferedEndSeconds = this.getBufferedEndSeconds();
            const durationSeconds = this.mainAudio.duration;
            const hasKnownDuration =
                Number.isFinite(durationSeconds) && durationSeconds > 0;
            const hasFinalMainDuration =
                Number.isFinite(state.mainDuration) && state.mainDuration > 0;
            const generationComplete =
                !state.isGenerating && hasFinalMainDuration;
            const isFullyBufferedShortTrack =
                generationComplete &&
                hasKnownDuration &&
                bufferedEndSeconds >=
                    durationSeconds - HLS_FULL_BUFFER_EPSILON_SECONDS;

            if (
                !warmupExpired &&
                bufferedEndSeconds <
                    HLS_JS_PRE_PLAY_BUFFER_SECONDS *
                        getNetworkBufferMultiplier() &&
                !isFullyBufferedShortTrack
            ) {
                meditationVerboseDebugLog(
                    "[AudioController] startHlsJsPlayback: waiting for warmup buffer",
                    {
                        bufferedEndSeconds,
                        requiredBufferedEndSeconds:
                            HLS_JS_PRE_PLAY_BUFFER_SECONDS *
                            getNetworkBufferMultiplier(),
                        durationSeconds: hasKnownDuration
                            ? durationSeconds
                            : null,
                        generationComplete,
                        hasFinalMainDuration,
                        warmupExpired,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                return;
            }

            playRequested = true;
            this.mainAudio.play().catch((err) => {
                if (err?.name === "AbortError") {
                    cleanupStartup();
                    meditationDebugWarn(
                        "[AudioController] startHlsJsPlayback play interrupted",
                        {
                            errorName: err?.name,
                            interruptionMessage: err?.message,
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        "warn",
                        "hls.js startup play() was interrupted before playback began.",
                    );
                    return;
                }

                // AIDEV-NOTE: NotAllowedError means the user gesture context
                // expired before play() ran (Android Chrome autoplay policy).
                // The audio is buffered via hls.js and ready — we just need a
                // fresh tap to unlock. Dispatch NEEDS_USER_GESTURE instead of
                // failing so the UI shows "Tap to listen". Do NOT destroy the
                // hls.js instance — the buffered segments are still valid.
                if (err?.name === "NotAllowedError") {
                    cleanupStartup();
                    this._awaitingUserGestureRecovery = true;
                    meditationDebugWarn(
                        "[AudioController] startHlsJsPlayback: NotAllowedError — needs fresh user gesture",
                        {
                            errorName: err?.name,
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        "warn",
                        "hls.js startup play() rejected by autoplay policy — awaiting user gesture.",
                    );
                    this.dispatch({ type: "NEEDS_USER_GESTURE" });
                    return;
                }

                cleanupStartup();
                console.error(
                    "[AudioController] startHlsJsPlayback play error",
                    {
                        errorName: err?.name,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "hls.js startup play() was rejected before playback began.",
                );
                this.destroyHlsJsInstance();
                this.failPlayback("Failed to load audio. Please try again.");
            });
        };

        const onLoadedMetadata = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            if (warmupTimeoutId === null) {
                warmupTimeoutId = setTimeout(() => {
                    warmupExpired = true;
                    meditationDebugWarn(
                        "[AudioController] startHlsJsPlayback: warmup timeout elapsed",
                        {
                            requiredBufferedEndSeconds:
                                HLS_JS_PRE_PLAY_BUFFER_SECONDS *
                                getNetworkBufferMultiplier(),
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                    maybeStartPlayback();
                }, HLS_JS_PRE_PLAY_WARMUP_TIMEOUT_MS);
            }
            maybeStartPlayback();
        };

        const onMediaError = () => {
            if (playbackStarted) return;

            cleanupStartup();
            console.error("[AudioController] startHlsJsPlayback media error", {
                ...this.getAudioDebugSnapshot(this.mainAudio),
            });
            this.logMediaError(
                this.mainAudio,
                "error",
                "hls.js startup hit an HTMLMediaElement error before playback began.",
            );
            this.destroyHlsJsInstance();
            this.failPlayback("Failed to load audio. Please try again.");
        };

        const onHlsError = (_event: string, data: unknown) => {
            const errorData = data as HlsErrorData;
            const breakType = this.mapHlsBreakType(errorData.details);
            if (this.shouldSuppressHlsJsBreak(errorData, breakType)) {
                return;
            }
            const logLevel = errorData.fatal ? "error" : "warn";
            this.logBreak(
                logLevel,
                breakType,
                this.getHlsErrorPayload(errorData),
                this.mainAudio,
            );

            if (!errorData.fatal) {
                return;
            }

            cleanupStartup();
            console.error(
                "[AudioController] startHlsJsPlayback fatal hls.js error",
                {
                    details: errorData.details ?? null,
                    type: errorData.type ?? null,
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                },
            );
            this.destroyHlsJsInstance();
            this.failPlayback("Failed to load audio. Please try again.");
        };

        // AIDEV-NOTE: Authoritative signal for "HLS manifest is finalized"
        // on hls.js. Fires when the media playlist has ENDLIST (i.e. no
        // longer live) and details.totalduration reflects the final length.
        // Once-guard in dispatchDurationDiscoveredOnce prevents repeats
        // across seek/rebuffer reloads.
        //
        // Subscribe to BOTH LEVEL_LOADED and LEVEL_UPDATED. hls.js emits
        // LEVEL_LOADED when a playlist file is fetched; LEVEL_UPDATED
        // fires afterwards and also when details change on a refresh poll.
        // Using both catches the finalization transition regardless of
        // which is last-to-fire in the specific version (1.6.x).
        //
        // Accept either `details.live === false` or `details.endList === true`
        // as the "finalized" signal — some hls.js code paths set one and
        // not the other until the next internal tick.
        const onHlsLevelDetails = (eventName: string, data: unknown) => {
            const details = (
                data as {
                    details?: {
                        live?: boolean;
                        endList?: boolean;
                        totalduration?: number;
                        url?: string;
                        fragments?: Array<unknown>;
                    };
                }
            ).details;
            const live = details?.live;
            const endList = details?.endList;
            const totalduration = details?.totalduration;
            meditationDebugLog("[AudioController] hls.js level event", {
                eventName,
                live,
                endList,
                totalduration,
                audioDuration: Number.isFinite(this.mainAudio.duration)
                    ? this.mainAudio.duration
                    : null,
                fragCount: details?.fragments?.length ?? null,
                alreadyDispatched: this._durationDiscoveredDispatched,
            });
            if (live === false || endList === true) {
                this.dispatchDurationDiscoveredOnce(
                    totalduration ?? this.mainAudio.duration,
                );
            }
        };

        this.mainAudio.addEventListener("loadedmetadata", onLoadedMetadata);
        this.mainAudio.addEventListener("loadeddata", maybeStartPlayback);
        this.mainAudio.addEventListener("canplay", maybeStartPlayback);
        this.mainAudio.addEventListener("canplaythrough", maybeStartPlayback);
        this.mainAudio.addEventListener("progress", maybeStartPlayback);
        this.mainAudio.addEventListener("durationchange", maybeStartPlayback);
        this.mainAudio.addEventListener("playing", onPlaying);
        this.mainAudio.addEventListener("error", onMediaError);
        hls.on(Hls.Events.ERROR, onHlsError);
        hls.on(Hls.Events.LEVEL_LOADED, onHlsLevelDetails);
        hls.on(Hls.Events.LEVEL_UPDATED, onHlsLevelDetails);

        timeoutId = setTimeout(() => {
            this.classifyAndHandleTimeout({
                cleanup: cleanupStartup,
                url,
                audibleStartThresholdSeconds:
                    HLS_JS_MEDITATION_START_POSITION_SECONDS,
                playRequested,
                playbackStarted,
                path: "hls-js",
                onExtend: (newId) => {
                    timeoutId = newId;
                },
                destroyHls: () => this.destroyHlsJsInstance(),
            });
        }, STARTUP_TIMEOUT);

        hls.loadSource(url);
        hls.attachMedia(this.mainAudio);

        this.mainAudio.addEventListener(
            "ended",
            () => {
                stopMonitor();
            },
            { once: true },
        );
    }

    /**
     * Single HLS startup attempt. Called by startHlsPlayback and by the retry path.
     */
    private attemptHlsStartup(
        url: string,
        audibleStartThresholdSeconds: number,
    ) {
        const attempt = this._hlsStartupRetryCount;
        const MAX_RETRIES = 2;
        const RETRY_DELAYS_MS = [1000, 2500];

        this.mainAudio.preload = "auto";
        meditationDebugLog(
            "[AudioController] startHlsPlayback: assigning src",
            {
                audioInstanceId: this.mainAudio.dataset.audioInstanceId,
                url: url.substring(0, 140),
                audibleStartThresholdSeconds,
                attempt,
                readyState: this.mainAudio.readyState,
                networkState: this.mainAudio.networkState,
            },
        );

        // AIDEV-NOTE: On retry, pause + clear src + load to fully reset Safari's
        // HLS state machine before re-assigning the playlist URL.
        if (attempt > 0) {
            this.expectPause();
            this.mainAudio.pause();
            this.mainAudio.removeAttribute("src");
            this.mainAudio.load();
        }

        this.mainAudio.src = url;
        this.attachUrlPlaybackDebugLogging(
            attempt > 0
                ? `startHlsPlayback:retry-${attempt}`
                : "startHlsPlayback",
        );

        const STARTUP_TIMEOUT = getStartupTimeoutMs();
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let playbackStarted = false;
        let playRequested = false;
        const requiredBufferedEndSeconds = Math.max(
            audibleStartThresholdSeconds +
                NATIVE_HLS_STARTUP_BUFFER_MARGIN_SECONDS *
                    getNetworkBufferMultiplier(),
            NATIVE_HLS_STARTUP_BUFFER_MARGIN_SECONDS *
                getNetworkBufferMultiplier(),
        );
        const stopMonitor = this.startUrlPlaybackStateMonitor(
            attempt > 0
                ? `startHlsPlayback:retry-${attempt}`
                : "startHlsPlayback",
        );

        const cleanup = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            this.mainAudio.removeEventListener(
                "loadeddata",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("canplay", maybeStartPlayback);
            this.mainAudio.removeEventListener(
                "canplaythrough",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("progress", maybeStartPlayback);
            this.mainAudio.removeEventListener(
                "durationchange",
                maybeStartPlayback,
            );
            this.mainAudio.removeEventListener("playing", onPlaying);
            this.mainAudio.removeEventListener("error", onError);
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            stopMonitor();
        };

        // Store cleanup so a future startHlsPlayback call can cancel a pending retry
        this._hlsStartupRetryCleanup = cleanup;

        const onPlaying = () => {
            playbackStarted = true;
            cleanup();
            this._hlsStartupRetryCleanup = null;
            if (attempt > 0) {
                meditationDebugLog(
                    "[AudioController] startHlsPlayback: recovered after retry",
                    { attempt },
                );
                // AIDEV-NOTE: Clear the reconnecting indicator on successful recovery
                this.dispatch({ type: "RECONNECT_SUCCESS" });
            }
            // AIDEV-NOTE: After startup succeeds, attach mid-playback stall
            // detection. Safari's native HLS retries segments automatically —
            // we just surface stalls to the UI via waiting/playing events.
            this.attachHlsStallDetection();
        };

        const maybeStartPlayback = () => {
            if (playRequested) {
                return;
            }

            const bufferedEndSeconds = this.getBufferedEndSeconds();
            if (bufferedEndSeconds < requiredBufferedEndSeconds) {
                meditationVerboseDebugLog(
                    "[AudioController] startHlsPlayback: waiting for native startup buffer",
                    {
                        attempt,
                        bufferedEndSeconds,
                        requiredBufferedEndSeconds,
                        audibleStartThresholdSeconds,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                return;
            }

            playRequested = true;
            // AIDEV-NOTE: Safari treats live HLS streams as starting at the
            // live edge, which may be 1-2s into the audio. Seek to the
            // beginning of the buffered range so the first words aren't lost.
            const buffered = this.mainAudio.buffered;
            if (buffered.length > 0) {
                const bufferStart = buffered.start(0);
                if (this.mainAudio.currentTime > bufferStart) {
                    meditationDebugLog(
                        "[AudioController] startHlsPlayback: seeking to buffer start",
                        {
                            currentTime: this.mainAudio.currentTime,
                            bufferStart,
                        },
                    );
                    this.mainAudio.currentTime = bufferStart;
                }
            }
            this.mainAudio.play().catch((err) => {
                if (err?.name === "AbortError") {
                    cleanup();
                    this._hlsStartupRetryCleanup = null;
                    meditationDebugWarn(
                        "[AudioController] startHlsPlayback play interrupted",
                        {
                            errorName: err?.name,
                            interruptionMessage: err?.message,
                            attempt,
                            bufferedEndSeconds,
                            requiredBufferedEndSeconds,
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        "warn",
                        "Native HLS startup play() was interrupted before playback began.",
                    );
                    return;
                }

                // AIDEV-NOTE: NotAllowedError means the user gesture context
                // expired during HLS buffering (iOS autoplay policy). The audio
                // is buffered and ready — we just need a fresh tap to unlock.
                // Dispatch NEEDS_USER_GESTURE instead of failing so the UI can
                // show a "Tap to listen" button.
                if (err?.name === "NotAllowedError") {
                    cleanup();
                    this._hlsStartupRetryCleanup = null;
                    this._awaitingUserGestureRecovery = true;
                    meditationDebugWarn(
                        "[AudioController] startHlsPlayback: NotAllowedError — needs fresh user gesture",
                        {
                            errorName: err?.name,
                            attempt,
                            bufferedEndSeconds,
                            requiredBufferedEndSeconds,
                            ...this.getAudioDebugSnapshot(this.mainAudio),
                        },
                    );
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        "warn",
                        "Native HLS startup play() rejected by autoplay policy — awaiting user gesture.",
                    );
                    this.dispatch({ type: "NEEDS_USER_GESTURE" });
                    return;
                }

                cleanup();
                this._hlsStartupRetryCleanup = null;
                console.error("[AudioController] startHlsPlayback play error", {
                    errorName: err?.name,
                    attempt,
                    bufferedEndSeconds,
                    requiredBufferedEndSeconds,
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "Native HLS startup play() was rejected before playback began.",
                );
                this.failPlayback("Failed to load audio. Please try again.");
            });
        };

        const onLoadedMetadata = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            this.maybeCaptureNativeHlsStartOffset();
            maybeStartPlayback();
        };

        const onError = () => {
            // Only retry startup failures (before playback has started)
            if (playbackStarted) return;
            if (this._awaitingUserGestureRecovery) {
                meditationDebugWarn(
                    "[AudioController] startHlsPlayback: ignoring startup media error while awaiting user gesture recovery",
                    this.getAudioDebugSnapshot(this.mainAudio),
                );
                cleanup();
                this._hlsStartupRetryCleanup = null;
                return;
            }

            cleanup();
            this._hlsStartupRetryCleanup = null;

            if (attempt < MAX_RETRIES) {
                const delay = RETRY_DELAYS_MS[attempt] ?? 2500;
                meditationDebugWarn(
                    "[AudioController] startHlsPlayback: startup error, retrying",
                    {
                        attempt,
                        nextAttempt: attempt + 1,
                        delayMs: delay,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                this.logMediaError(
                    this.mainAudio,
                    "warn",
                    "Native HLS startup hit an HTMLMediaElement error and is retrying.",
                );
                this.dispatch({ type: "RECONNECTING" });
                this._hlsStartupRetryCount = attempt + 1;
                const retryTimeoutId = setTimeout(() => {
                    this.attemptHlsStartup(url, audibleStartThresholdSeconds);
                }, delay);
                // Allow cleanup to cancel the pending retry
                this._hlsStartupRetryCleanup = () => {
                    clearTimeout(retryTimeoutId);
                };
            } else {
                console.error(
                    "[AudioController] startHlsPlayback media error (retries exhausted)",
                    {
                        totalAttempts: attempt + 1,
                        ...this.getAudioDebugSnapshot(this.mainAudio),
                    },
                );
                this.logMediaError(
                    this.mainAudio,
                    "error",
                    "Native HLS startup exhausted retries after HTMLMediaElement errors.",
                );
                this.failPlayback("Failed to load audio. Please try again.");
            }
        };

        this.mainAudio.addEventListener("loadedmetadata", onLoadedMetadata);
        this.mainAudio.addEventListener("loadeddata", maybeStartPlayback);
        this.mainAudio.addEventListener("canplay", maybeStartPlayback);
        this.mainAudio.addEventListener("canplaythrough", maybeStartPlayback);
        this.mainAudio.addEventListener("progress", maybeStartPlayback);
        this.mainAudio.addEventListener("durationchange", maybeStartPlayback);
        this.mainAudio.addEventListener("playing", onPlaying);
        this.mainAudio.addEventListener("error", onError);

        timeoutId = setTimeout(() => {
            this.classifyAndHandleTimeout({
                cleanup: () => {
                    cleanup();
                    this._hlsStartupRetryCleanup = null;
                },
                url,
                audibleStartThresholdSeconds,
                playRequested,
                playbackStarted,
                path: "native-hls",
                onExtend: (newId) => {
                    timeoutId = newId;
                },
            });
        }, STARTUP_TIMEOUT);

        this.mainAudio.addEventListener(
            "ended",
            () => {
                stopMonitor();
            },
            { once: true },
        );
    }

    /**
     * Attach mid-playback stall detection for native HLS.
     * AIDEV-NOTE: Safari's native HLS player retries segments automatically
     * on network drops. We listen for `waiting` (stall) and `playing` (recovery)
     * to surface buffering state to the UI. No manual retry needed mid-playback.
     */
    private attachHlsStallDetection() {
        if (!this._nativeHlsPlayback && !this._hlsJsPlayback) return;

        const onWaiting = () => {
            const state = this.getState();
            if (state.phase !== "main" || !state.isPlaying) return;
            if (this._stallStartedAtMs !== null) return;
            this._stallStartedAtMs = Date.now();
            meditationDebugLog(
                "[AudioController] HLS mid-playback stall detected",
                this.getAudioDebugSnapshot(this.mainAudio),
            );
            this.dispatch({ type: "RECONNECTING" });
        };

        const onPlayingAfterStall = () => {
            const state = this.getState();
            if (!state.isReconnecting) return;
            meditationDebugLog(
                "[AudioController] HLS mid-playback stall recovered",
                {
                    currentTime: this.mainAudio.currentTime,
                },
            );
            this.dispatch({ type: "RECONNECT_SUCCESS" });
        };

        const onEnded = () => {
            this._stallStartedAtMs = null;
            this.mainAudio.removeEventListener("waiting", onWaiting);
            this.mainAudio.removeEventListener("playing", onPlayingAfterStall);
        };

        this.mainAudio.addEventListener("waiting", onWaiting);
        this.mainAudio.addEventListener("playing", onPlayingAfterStall);
        this.mainAudio.addEventListener("ended", onEnded, { once: true });
    }

    /**
     * Resume playback from a cached URL after an SSE reconnect.
     * AIDEV-NOTE: This bypasses MediaSource replay and seeks into the cached file
     * so reconnects continue from the last known playback position.
     */
    resumeFromUrl(url: string, seekTime: number) {
        this._fallbackMode = true;
        this.resetFallbackPlaybackProfile();
        this.mainAudio.src = url;
        this.mainAudio.preload = "metadata";
        this.attachUrlPlaybackDebugLogging("resumeFromUrl");

        const RESUME_TIMEOUT = 10_000;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const onLoadedMetadata = () => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            const safeDuration = Number.isFinite(this.mainAudio.duration)
                ? this.mainAudio.duration
                : seekTime + 1;
            const clampedTime = Math.min(
                seekTime,
                Math.max(0, safeDuration - 1),
            );
            this.mainAudio.currentTime = clampedTime;

            this.mainAudio.play().catch((err) => {
                // AIDEV-NOTE: Log error details + audio state for iOS Safari diagnosis
                console.error("[AudioController] resumeFromUrl play error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                    currentSrc: this.mainAudio.currentSrc,
                    seekTime,
                    clampedTime,
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "resumeFromUrl() play() was rejected while recovering playback.",
                );
                this.failPlayback(
                    "Failed to resume playback. Please try again.",
                );
            });
        };

        this.mainAudio.addEventListener("loadedmetadata", onLoadedMetadata);

        timeoutId = setTimeout(() => {
            this.mainAudio.removeEventListener(
                "loadedmetadata",
                onLoadedMetadata,
            );
            this.logBreak(
                "error",
                "startup_timeout",
                {
                    errorMessage:
                        "resumeFromUrl() did not receive loadedmetadata before the resume timeout.",
                },
                this.mainAudio,
            );
            this.failPlayback("Failed to resume playback. Please try again.");
        }, RESUME_TIMEOUT);
    }

    /**
     * Unlock the audio element for playback on iOS Safari.
     * AIDEV-NOTE: iOS Safari requires play() to be called directly from a user gesture.
     * This method must be called synchronously within the click/tap handler to "unlock"
     * the audio element for future programmatic playback.
     *
     * Call this FIRST in the start() flow, before any async operations.
     */
    unlockAudioForMobile(): Promise<void> {
        const state = this.getState();
        const hasActiveFallbackPlayback =
            Boolean(this.mainAudio.currentSrc) &&
            (this._fallbackMode ||
                state.isLoading ||
                state.isGenerating ||
                state.isPlaying ||
                state.phase === "intro" ||
                state.phase === "gap" ||
                state.phase === "main" ||
                this.mainAudio.readyState >= HTMLMediaElement.HAVE_METADATA);

        meditationVerboseDebugLog(
            "[AudioController] unlockAudioForMobile: starting",
            {
                readyState: this.mainAudio.readyState,
                networkState: this.mainAudio.networkState,
                fallbackMode: this._fallbackMode,
                phase: state.phase,
                isLoading: state.isLoading,
                isGenerating: state.isGenerating,
                isPlaying: state.isPlaying,
            },
        );
        // AIDEV-NOTE: Once fallback/HLS playback has a real src, re-running the
        // silent unlock sequence against that same element can poison it and
        // trigger a duplicate bootstrap/generation loop. Treat active fallback
        // playback as already unlocked and leave the element untouched.
        if (hasActiveFallbackPlayback) {
            meditationVerboseDebugLog(
                "[AudioController] unlockAudioForMobile: skipping because main audio is already active",
                {
                    currentSrc: this.mainAudio.currentSrc,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    fallbackMode: this._fallbackMode,
                    phase: state.phase,
                    isLoading: state.isLoading,
                    isGenerating: state.isGenerating,
                    isPlaying: state.isPlaying,
                },
            );
            return Promise.resolve();
        }
        // AIDEV-NOTE: Playing a tiny bit of silence unlocks the audio element on iOS.
        // We use a data URI for a minimal silent audio to avoid network requests.
        // After this, subsequent play() calls will work even if not in a user gesture.
        meditationVerboseDebugLog(
            "[AudioController] unlockAudioForMobile: starting unlock attempt",
        );

        const silentDataUri =
            "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+9DEAAAIAANIAAAAgAAA0gAAABBHCf/+CAIAgCAIYgB8oCGKAhiwIAh/KAgDEAQxIEMQBAEP/lT//5QEAYgCAMf/ygIYkCH/+UBAEP/5QEARAEAQBD//5Q//Lgg//iAIf/ygJAYoCGIAh//lAQ//lAQxIEP/8oCGLAhiQIAgCGJAhiAIf/ygIf/5UwAAA//tQxAADwAADSAAAAIAAANIAAAAQAAAD/wAAD/4AAB+AA//AAAPwAAD8AAAA/+UBD//KAhigIf/5QEAQBD/+UBD//KBAGP/8oCH/8oCQGJAh//lAQBAEMWBDFgQ//lAQ//lAQ//lASAxIEMSBDEAQ//ygIf/5QEMSBD//KAh//lAQA=";

        this.mainAudio.src = silentDataUri;
        this.mainAudio.volume = 0;

        // AIDEV-NOTE: play() can throw synchronously in some environments
        // (e.g., Chromium autoplay policy). Catch it before Promise.race.
        let playPromise: Promise<void> | undefined;
        try {
            playPromise = this.mainAudio.play();
        } catch (err) {
            meditationDebugWarn(
                "[AudioController] unlockAudioForMobile: play() threw synchronously, replacing element",
                {
                    errorName: (err as Error)?.name,
                    errorMessage: (err as Error)?.message,
                },
            );
            this.mainAudio.volume = 1;
            this.replaceMainAudioElement();
            return Promise.resolve();
        }
        if (!playPromise) {
            meditationVerboseDebugLog(
                "[AudioController] unlockAudioForMobile: no play promise (old browser)",
            );
            this.mainAudio.volume = 1;
            return Promise.resolve();
        }

        // AIDEV-NOTE: Chromium play() on a silent data URI can hang forever. A 3s
        // timeout prevents blocking start(). The `settled` flag ensures only the
        // first resolver (timeout or play promise) acts — without it the late
        // arrival would destroy HLS playback that start() already initiated.
        const UNLOCK_TIMEOUT_MS = 3000;
        let settled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const timeoutPromise = new Promise<void>((resolve) => {
            timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                meditationVerboseDebugLog(
                    "[AudioController] unlockAudioForMobile: timed out, treating as no-op",
                );
                // Clean up the silent source so it doesn't interfere with HLS
                this.mainAudio.pause();
                this.mainAudio.removeAttribute("src");
                this.mainAudio.volume = 1;
                resolve();
            }, UNLOCK_TIMEOUT_MS);
        });

        return Promise.race([
            playPromise
                .then(() => {
                    if (settled) return;
                    settled = true;
                    if (timeoutId !== null) clearTimeout(timeoutId);
                    meditationVerboseDebugLog(
                        "[AudioController] unlockAudioForMobile: unlock succeeded",
                    );
                    this.mainAudio.pause();
                    this.mainAudio.currentTime = 0;
                    this.mainAudio.volume = 1;
                })
                .catch((err) => {
                    if (settled) return;
                    settled = true;
                    if (timeoutId !== null) clearTimeout(timeoutId);
                    meditationDebugWarn(
                        "[AudioController] unlockAudioForMobile: unlock failed, replacing audio element",
                        {
                            errorName: err?.name,
                            errorMessage: err?.message,
                            readyState: this.mainAudio.readyState,
                            networkState: this.mainAudio.networkState,
                        },
                    );
                    // AIDEV-NOTE: After a failed unlock, the audio element is poisoned
                    // (networkState=3 NETWORK_NO_SOURCE). Setting a new src on a poisoned
                    // element doesn't fully reset it — Safari will fire MEDIA_ERR_SRC_NOT_SUPPORTED.
                    // Fix: replace the element entirely with a fresh one.
                    this.replaceMainAudioElement();
                }),
            timeoutPromise,
        ]);
    }

    /**
     * Replace the main audio element with a fresh one.
     * AIDEV-NOTE: Used after a failed unlock attempt to avoid the poisoned
     * networkState=3 state that prevents any future playback.
     */
    private replaceMainAudioElement() {
        this.destroyHlsJsInstance();
        const oldElement = this.mainAudio;
        const oldInstanceId = this._mainAudioInstanceId;

        // Create fresh element
        this.mainAudio = new Audio();
        this._mainAudioInstanceId += 1;
        this.mainAudio.preload = "auto";
        this.mainAudio.style.display = "none";
        this.mainAudio.setAttribute("data-meditation-audio", "main");
        this.mainAudio.setAttribute(
            "data-audio-instance-id",
            this.getMainAudioInstanceLabel(),
        );
        document.body.appendChild(this.mainAudio);

        // Re-attach event listeners (same as constructor's setupEventListeners)
        this.setupMainEventListeners();

        // Remove old element
        oldElement.pause();
        oldElement.removeAttribute("src");
        oldElement.load(); // Release resources
        if (oldElement.parentNode) {
            oldElement.parentNode.removeChild(oldElement);
        }

        meditationVerboseDebugLog(
            "[AudioController] replaceMainAudioElement: fresh element created",
            {
                oldInstanceId: `main-audio-${oldInstanceId}`,
                newInstanceId: this.getMainAudioInstanceLabel(),
                readyState: this.mainAudio.readyState,
                networkState: this.mainAudio.networkState,
            },
        );
    }

    /**
     * Start direct streaming playback from the /api/meditation/audio endpoint.
     * AIDEV-NOTE: This is used in fallback mode (iOS Safari) to enable progressive
     * playback without waiting for full generation. Unlike playFromUrl(), this
     * uses chunked transfer encoding to start playback while generating.
     *
     * IMPORTANT: unlockAudioForMobile() must be called BEFORE this method
     * to ensure play() works on iOS Safari.
     *
     * @param url - The direct audio streaming endpoint URL
     */
    startDirectStreaming(url: string) {
        meditationDebugLog("[AudioController] startDirectStreaming: called", {
            url: url.substring(0, 100),
            mainAudioInstanceId: this.getMainAudioInstanceLabel(),
            readyState: this.mainAudio.readyState,
            networkState: this.mainAudio.networkState,
            currentSrc: this.mainAudio.currentSrc,
        });
        // AIDEV-NOTE: Single-flight guard — if we're already streaming this URL,
        // don't send another request. iOS WebKit can trigger this via range probes.
        if (this._directStreamingUrl === url) {
            meditationDebugLog(
                "[AudioController] startDirectStreaming: blocked by single-flight guard",
            );
            return;
        }
        this._directStreamingUrl = url;

        // AIDEV-NOTE: If the supposedly fresh element is already at
        // NETWORK_NO_SOURCE before we assign the real stream URL, Safari has
        // carried poisoned state across the prior unlock/replacement cycle.
        // Replace it one more time so we can distinguish stream failures from
        // element-lifecycle failures in logs.
        if (
            this.mainAudio.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
        ) {
            meditationDebugWarn(
                "[AudioController] startDirectStreaming: element already poisoned before src assignment, replacing again",
                {
                    mainAudioInstanceId: this.getMainAudioInstanceLabel(),
                    ...this.getAudioDebugSnapshot(this.mainAudio),
                },
            );
            this.replaceMainAudioElement();
        }

        // AIDEV-NOTE: Do NOT call .load() after setting .src — on iOS WebKit,
        // .load() aborts the in-flight request triggered by .src and starts a new one,
        // causing duplicate server-side meditation generation. Setting .src alone
        // invokes the media load algorithm per the WHATWG spec.
        // AIDEV-NOTE: Use preload="auto" so Safari starts fetching immediately and
        // fires canplay. The single-flight guard above prevents duplicate
        // startDirectStreaming calls, and server-side dedup prevents duplicate generation.
        this.mainAudio.preload = "auto";
        this.mainAudio.src = url;
        this.attachUrlPlaybackDebugLogging("startDirectStreaming");
        meditationDebugLog(
            "[AudioController] startDirectStreaming: src assigned",
            {
                url: url.substring(0, 100),
                mainAudioInstanceId: this.getMainAudioInstanceLabel(),
                ...this.getAudioDebugSnapshot(this.mainAudio),
            },
        );

        // AIDEV-NOTE: Dispatch CHUNK_RECEIVED to enter generating state.
        // This shows the breathing animation while Safari buffers.
        // The native 'play' event listener will dispatch MAIN_PLAYBACK_STARTED
        // when playback actually starts, transitioning to the histogram view.
        this.dispatch({ type: "CHUNK_RECEIVED" });

        // AIDEV-NOTE: Use loadedmetadata instead of canplay for chunked streaming.
        // canplay requires enough buffered audio data, which may never accumulate
        // fast enough for iOS Safari with a slow chunked-transfer stream.
        // loadedmetadata fires once Safari understands the fMP4 container, then
        // play() triggers progressive playback as more chunks arrive.
        const STARTUP_TIMEOUT = getStartupTimeoutMs();
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const stopMonitor = this.startUrlPlaybackStateMonitor(
            "startDirectStreaming",
        );

        const startedAt = Date.now();
        let playbackStarted = false;
        let playRequested = false;

        // AIDEV-NOTE: Clear timeout only when playback actually starts (playing event),
        // not on loadedmetadata. loadedmetadata fires after the fMP4 init segment is
        // parsed but before any audio is buffered — a stream stall after metadata
        // would hang forever if we cleared the timeout here.
        const clearStartupTimeout = () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const onPlaying = () => {
            meditationDebugLog(
                "[AudioController] startDirectStreaming: playing started",
                { elapsedMs: Date.now() - startedAt },
            );
            playbackStarted = true;
            this.mainAudio.removeEventListener("playing", onPlaying);
            clearStartupTimeout();
        };
        this.mainAudio.addEventListener("playing", onPlaying);

        const onReady = () => {
            meditationVerboseDebugLog(
                "[AudioController] startDirectStreaming: loadedmetadata fired",
                {
                    elapsedMs: Date.now() - startedAt,
                    readyState: this.mainAudio.readyState,
                    duration: this.mainAudio.duration,
                },
            );
            this.mainAudio.removeEventListener("loadedmetadata", onReady);
            // Don't clear timeout here — wait for playing event

            playRequested = true;
            this.mainAudio.play().catch((err) => {
                console.error(
                    "[AudioController] startDirectStreaming play error:",
                    err?.name,
                    err?.message,
                );
                this.mainAudio.removeEventListener("playing", onPlaying);
                clearStartupTimeout();
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "error",
                    "Direct streaming play() was rejected before playback began.",
                );
                this.failPlayback(
                    "Failed to start audio streaming. Please try again.",
                );
            });
        };
        this.mainAudio.addEventListener("loadedmetadata", onReady);

        // AIDEV-NOTE: Listen for audio element errors (401, 404, network failures).
        // Without this, a failed fetch silently hangs at the breathing animation
        // until the 60s timeout — the user sees no feedback.
        const onError = () => {
            this.mainAudio.removeEventListener("loadedmetadata", onReady);
            this.mainAudio.removeEventListener("playing", onPlaying);
            this.mainAudio.removeEventListener("error", onError);
            clearStartupTimeout();
            console.error(
                "[AudioController] startDirectStreaming media error",
                this.getAudioDebugSnapshot(this.mainAudio),
            );
            stopMonitor();
            this.logMediaError(
                this.mainAudio,
                "error",
                "Direct streaming hit an HTMLMediaElement error before playback began.",
            );
            this.failPlayback("Failed to load audio. Please try again.");
        };
        this.mainAudio.addEventListener("error", onError);

        timeoutId = setTimeout(() => {
            this.classifyAndHandleTimeout({
                cleanup: () => {
                    this.mainAudio.removeEventListener(
                        "loadedmetadata",
                        onReady,
                    );
                    this.mainAudio.removeEventListener("playing", onPlaying);
                    this.mainAudio.removeEventListener("error", onError);
                    stopMonitor();
                },
                url: this.mainAudio.src,
                audibleStartThresholdSeconds: 0,
                playRequested,
                playbackStarted,
                path: "direct",
                onExtend: (newId) => {
                    timeoutId = newId;
                },
            });
        }, STARTUP_TIMEOUT);

        this.mainAudio.addEventListener(
            "ended",
            () => {
                stopMonitor();
            },
            { once: true },
        );
    }

    /**
     * Play audio based on current phase
     * AIDEV-NOTE: In fallback mode (iOS Safari), always use mainAudio
     */
    play() {
        const state = this.getState();

        // AIDEV-NOTE: In fallback mode, mainAudio is the only element used
        if (this._fallbackMode) {
            this.mainAudio.play().catch((err) => {
                console.error("[AudioController] play (fallback) error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                    currentSrc: this.mainAudio.currentSrc,
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "warn",
                    "Manual play() on fallback audio was rejected by the browser.",
                );
            });
            return;
        }

        switch (state.phase) {
            case "intro":
                this.introAudio.play().catch((err) => {
                    console.error("[AudioController] play (intro) error:", {
                        errorName: err?.name,
                        errorMessage: err?.message,
                        readyState: this.introAudio.readyState,
                        networkState: this.introAudio.networkState,
                        paused: this.introAudio.paused,
                        currentSrc: this.introAudio.currentSrc,
                        mediaSourceState:
                            this.introMediaSource?.readyState ?? "none",
                    });
                    this.logPlayRejected(
                        this.introAudio,
                        err,
                        "warn",
                        "Manual play() on intro audio was rejected by the browser.",
                    );
                });
                break;
            case "main":
                this.mainAudio.play().catch((err) => {
                    console.error("[AudioController] play (main) error:", {
                        errorName: err?.name,
                        errorMessage: err?.message,
                        readyState: this.mainAudio.readyState,
                        networkState: this.mainAudio.networkState,
                        paused: this.mainAudio.paused,
                        currentSrc: this.mainAudio.currentSrc,
                        mediaSourceState:
                            this.mainMediaSource?.readyState ?? "none",
                    });
                    this.logPlayRejected(
                        this.mainAudio,
                        err,
                        this._audioUrl && err.name === "NotSupportedError"
                            ? "warn"
                            : "error",
                        "Manual play() on main audio was rejected by the browser.",
                    );
                    // AIDEV-NOTE: Fall back to URL if MediaSource format is wrong
                    if (this._audioUrl && err.name === "NotSupportedError") {
                        this.playFromUrl(this._audioUrl);
                    }
                });
                break;
            case "complete":
                // Restart from beginning
                this.introAudio.currentTime = 0;
                this.introAudio.play().catch((err) => {
                    console.error("[AudioController] play (complete) error:", {
                        errorName: err?.name,
                        errorMessage: err?.message,
                        readyState: this.introAudio.readyState,
                        networkState: this.introAudio.networkState,
                        paused: this.introAudio.paused,
                        currentSrc: this.introAudio.currentSrc,
                    });
                    this.logPlayRejected(
                        this.introAudio,
                        err,
                        "warn",
                        "Manual replay from complete state was rejected by the browser.",
                    );
                });
                break;
        }
    }

    /**
     * Pause audio based on current phase
     * AIDEV-NOTE: In fallback mode (iOS Safari), always try to pause mainAudio
     * since that's the only element used. Also pause both elements as a safety
     * net in case state is stale.
     */
    pause() {
        const state = this.getState();

        // AIDEV-NOTE: In fallback mode, mainAudio is the only element used
        // Always try to pause it regardless of phase state (which may be stale)
        if (this._fallbackMode) {
            this.expectPause();
            this.mainAudio.pause();
            return;
        }

        if (state.phase === "intro") {
            this.expectPause();
            this.introAudio.pause();
        } else if (state.phase === "main") {
            this.expectPause();
            this.mainAudio.pause();
        } else {
            meditationVerboseDebugWarn(
                "[AudioController] pause() called but phase is:",
                state.phase,
            );
        }
    }

    /**
     * Stop all playback and cleanup
     */
    stop() {
        // Clear gap timer
        if (this.gapTimeoutId) {
            clearTimeout(this.gapTimeoutId);
            this.gapTimeoutId = null;
        }

        // Stop audio
        this.expectPause(1500);
        this.introAudio.pause();
        this.mainAudio.pause();

        // Cleanup MediaSources
        this.cleanupIntroMediaSource();
        this.cleanupMainMediaSource();

        // Reset state
        this.introPendingChunks = [];
        this.introTotalBytes = 0;
        this.introStreamEnded = false;
        this.hasNotifiedIntroReady = false;
        this.introPlaybackStarted = false;

        this.mainPendingChunks = [];
        this.mainTotalBytes = 0;
        this.mainStreamEnded = false;
        this.hasNotifiedMainReady = false;
        this.mainPlaybackStarted = false;
        this.destroyHlsJsInstance();
        this.resetFallbackPlaybackProfile();
        // AIDEV-NOTE: EVO-1813 — drop any pending/active UI stall on teardown.
        // STOP/START/ERROR reducer transitions reset isStalled in state.
        this.stallDetector.reset();
        this._audioUrl = null;
        this._directStreamingUrl = null;
        this._playbackStartedAtMs = null;
        this._loggedPlayingSessionId = null;
    }

    /**
     * Seek to a specific time position
     */
    seek(time: number) {
        const state = this.getState();
        if (this._nativeHlsPlayback) {
            this.maybeCaptureNativeHlsStartOffset();
            this.mainAudio.currentTime =
                time + (this._nativeHlsStartOffsetSeconds ?? 0);
            return;
        }

        if (
            this._fallbackMode &&
            (this._hlsJsPlayback || state.introDuration === 0)
        ) {
            this.mainAudio.currentTime = time;
            this.mainAudio.play().catch((err) => {
                console.error("[AudioController] seek (fallback) play error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "warn",
                    "seek() re-play on fallback audio was rejected by the browser.",
                );
            });
            return;
        }

        const introEnd = state.introDuration;
        const gapEnd = introEnd + GAP_DURATION;

        if (time < introEnd) {
            // Seek in intro
            this.expectPause();
            this.mainAudio.pause();
            this.introAudio.currentTime = time;
            this.introAudio.play().catch((err) => {
                console.error("[AudioController] seek (intro) play error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.introAudio.readyState,
                    networkState: this.introAudio.networkState,
                    paused: this.introAudio.paused,
                });
                this.logPlayRejected(
                    this.introAudio,
                    err,
                    "warn",
                    "seek() re-play on intro audio was rejected by the browser.",
                );
            });
        } else if (time < gapEnd) {
            // In gap - go to start of main
            this.expectPause();
            this.introAudio.pause();
            this.mainAudio.currentTime = 0;
            this.mainAudio.play().catch((err) => {
                console.error("[AudioController] seek (gap) play error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "warn",
                    "seek() re-play at the gap boundary was rejected by the browser.",
                );
            });
        } else {
            // Seek in main
            this.expectPause();
            this.introAudio.pause();
            this.mainAudio.currentTime = time - gapEnd;
            this.mainAudio.play().catch((err) => {
                console.error("[AudioController] seek (main) play error:", {
                    errorName: err?.name,
                    errorMessage: err?.message,
                    readyState: this.mainAudio.readyState,
                    networkState: this.mainAudio.networkState,
                    paused: this.mainAudio.paused,
                });
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    "warn",
                    "seek() re-play on main audio was rejected by the browser.",
                );
            });
        }
    }

    /**
     * Cleanup all resources
     */
    cleanup() {
        this.stop();

        // Remove audio elements from DOM
        if (this.introAudio.parentNode) {
            this.introAudio.parentNode.removeChild(this.introAudio);
        }
        if (this.mainAudio.parentNode) {
            this.mainAudio.parentNode.removeChild(this.mainAudio);
        }
    }

    // --- Private Helpers ---

    /**
     * Process pending intro chunks - append to SourceBuffer if ready
     */
    private processIntroPendingChunks() {
        if (!this.introSourceBuffer || this.introSourceBuffer.updating) return;

        if (this.introPendingChunks.length === 0) {
            // No more chunks - check if we should end the stream
            if (
                this.introStreamEnded &&
                this.introMediaSource?.readyState === "open"
            ) {
                // AIDEV-NOTE: 10ms fade-out + next-tick delay before endOfStream()
                // prevents pop/click at clip boundary (especially on Safari).
                this.fadeOutAndEndStream(
                    this.introAudio,
                    this.introMediaSource,
                );
            }
            return;
        }

        const chunk = this.introPendingChunks.shift()!;
        try {
            // Create ArrayBuffer slice to satisfy TypeScript's strict BufferSource type
            const buffer = chunk.buffer.slice(
                chunk.byteOffset,
                chunk.byteOffset + chunk.byteLength,
            ) as ArrayBuffer;
            this.introSourceBuffer.appendBuffer(buffer);
        } catch (err) {
            console.error(
                "[AudioController] Failed to append intro chunk:",
                err,
            );
        }
    }

    /**
     * Process pending main chunks - append to SourceBuffer if ready
     */
    private processMainPendingChunks() {
        if (!this.mainSourceBuffer || this.mainSourceBuffer.updating) return;

        if (this.mainPendingChunks.length === 0) {
            // No more chunks - check if we should end the stream
            if (
                this.mainStreamEnded &&
                this.mainMediaSource?.readyState === "open"
            ) {
                // AIDEV-NOTE: 10ms fade-out + next-tick delay before endOfStream()
                // prevents pop/click at clip boundary (especially on Safari).
                this.fadeOutAndEndStream(this.mainAudio, this.mainMediaSource);
            }
            return;
        }

        const chunk = this.mainPendingChunks.shift()!;
        try {
            // Create ArrayBuffer slice to satisfy TypeScript's strict BufferSource type
            const buffer = chunk.buffer.slice(
                chunk.byteOffset,
                chunk.byteOffset + chunk.byteLength,
            ) as ArrayBuffer;
            this.mainSourceBuffer.appendBuffer(buffer);
        } catch (err) {
            console.error(
                "[AudioController] Failed to append main chunk:",
                err,
            );
        }
    }

    /**
     * Check if intro has enough buffered data to start playback
     */
    private maybeStartIntroPlayback() {
        if (this.introPlaybackStarted) return;
        // AIDEV-NOTE: When suppressed, chunks buffer but playback doesn't start.
        // unsuppressPlayback() will call this again when the user is ready.
        if (this._playbackSuppressed) return;
        if (!this.introSourceBuffer) return;

        // AIDEV-NOTE: Skip intro playback if no intro audio was received.
        // This happens with input streaming where there's no separate intro.
        if (this.introTotalBytes === 0 && this.introStreamEnded) {
            this.introPlaybackStarted = true; // Mark as started to prevent future attempts
            return;
        }

        const buffered = this.introSourceBuffer.buffered;
        const hasEnoughBuffer =
            buffered.length > 0 && buffered.end(0) >= MIN_BUFFER_DURATION;

        // Start playback when we have enough buffer OR stream ended
        if (hasEnoughBuffer || this.introStreamEnded) {
            this.introPlaybackStarted = true;

            this.introAudio.play().catch((err) => {
                // AIDEV-NOTE: Log error details + audio state for iOS Safari diagnosis
                console.error(
                    "[AudioController] Failed to start intro playback:",
                    {
                        errorName: err?.name,
                        errorMessage: err?.message,
                        readyState: this.introAudio.readyState,
                        networkState: this.introAudio.networkState,
                        paused: this.introAudio.paused,
                        currentSrc: this.introAudio.currentSrc,
                        mediaSourceState:
                            this.introMediaSource?.readyState ?? "none",
                        hasEnoughBuffer,
                        introStreamEnded: this.introStreamEnded,
                    },
                );
                this.logPlayRejected(
                    this.introAudio,
                    err,
                    "error",
                    "Intro MediaSource playback could not start after buffering.",
                );
            });
        }
    }

    /**
     * Check if main has enough buffered data to start playback
     */
    private maybeStartMainPlayback() {
        if (this.mainPlaybackStarted) return;
        // AIDEV-NOTE: When suppressed, chunks buffer but playback doesn't start.
        // unsuppressPlayback() will call this again when the user is ready.
        if (this._playbackSuppressed) return;
        if (!this.mainSourceBuffer) return;

        const state = this.getState();
        // AIDEV-NOTE: Allow main playback in:
        // - gap phase (after intro ends)
        // - main phase (already playing)
        // - idle phase when no intro was received (input streaming without intro)
        const noIntroReceived = this.introTotalBytes === 0;
        const canStartMain =
            state.phase === "gap" ||
            state.phase === "main" ||
            (state.phase === "idle" && noIntroReceived);

        if (!canStartMain) return;

        const buffered = this.mainSourceBuffer.buffered;
        const hasEnoughBuffer =
            buffered.length > 0 && buffered.end(0) >= MIN_BUFFER_DURATION;

        // Start playback when we have enough buffer OR stream ended
        if (hasEnoughBuffer || this.mainStreamEnded) {
            this.mainPlaybackStarted = true;

            // Dispatch to update state machine
            this.dispatch({ type: "MAIN_PLAYBACK_STARTED" });

            this.mainAudio.play().catch((err) => {
                // AIDEV-NOTE: Log error details + audio state for iOS Safari diagnosis
                console.error(
                    "[AudioController] Failed to start main playback:",
                    {
                        errorName: err?.name,
                        errorMessage: err?.message,
                        readyState: this.mainAudio.readyState,
                        networkState: this.mainAudio.networkState,
                        paused: this.mainAudio.paused,
                        currentSrc: this.mainAudio.currentSrc,
                        mediaSourceState:
                            this.mainMediaSource?.readyState ?? "none",
                        hasEnoughBuffer,
                        mainStreamEnded: this.mainStreamEnded,
                        hasAudioUrl: !!this._audioUrl,
                    },
                );
                this.logPlayRejected(
                    this.mainAudio,
                    err,
                    this._audioUrl && err.name === "NotSupportedError"
                        ? "warn"
                        : "error",
                    "Main MediaSource playback could not start after buffering.",
                );
                // AIDEV-NOTE: If MediaSource can't decode the cached format (e.g., MP3 fed
                // to fMP4/AAC SourceBuffer), fall back to URL-based playback.
                if (this._audioUrl && err.name === "NotSupportedError") {
                    console.warn(
                        "[AudioController] Falling back to URL playback",
                    );
                    this.playFromUrl(this._audioUrl);
                }
            });
        }
    }

    /**
     * Notify state machine that intro is ready (enough data buffered)
     */
    private maybeNotifyIntroReady() {
        if (this.hasNotifiedIntroReady) return;

        if (
            this.introTotalBytes >= READY_THRESHOLD_BYTES ||
            this.introStreamEnded
        ) {
            this.hasNotifiedIntroReady = true;
            this.dispatch({ type: "INTRO_READY" });
        }
    }

    /**
     * Notify state machine that main is ready (enough data buffered)
     */
    private maybeNotifyMainReady() {
        if (this.hasNotifiedMainReady) return;

        if (
            this.mainTotalBytes >= READY_THRESHOLD_BYTES ||
            this.mainStreamEnded
        ) {
            this.hasNotifiedMainReady = true;
            this.dispatch({ type: "MAIN_READY" });

            const state = this.getState();
            // AIDEV-NOTE: Try to start main playback in:
            // - gap phase (after intro ends)
            // - idle phase when no intro received (input streaming without intro)
            const noIntroReceived = this.introTotalBytes === 0;
            if (
                state.phase === "gap" ||
                (state.phase === "idle" && noIntroReceived)
            ) {
                this.tryPlayMain();
            }
        }
    }

    /**
     * AIDEV-NOTE: 10ms volume fade-out then endOfStream() on next tick.
     * Prevents audible pop/click at the clip boundary by:
     * 1. Ramping volume to 0 over 10ms (smooths any waveform discontinuity)
     * 2. Calling endOfStream() on next tick (gives browser time to finalize decode)
     * Volume is restored to 1 after endOfStream() so replay works normally.
     */
    private fadeOutAndEndStream(
        audio: HTMLAudioElement,
        mediaSource: MediaSource,
    ) {
        const originalVolume = audio.volume;
        const FADE_MS = 10;
        const STEPS = 5;
        const stepMs = FADE_MS / STEPS;
        let step = 0;

        const fadeInterval = window.setInterval(() => {
            step++;
            audio.volume = originalVolume * (1 - step / STEPS);

            if (step >= STEPS) {
                clearInterval(fadeInterval);
                audio.volume = 0;

                // Next-tick delay before endOfStream for Safari robustness
                setTimeout(() => {
                    try {
                        if (mediaSource.readyState === "open") {
                            mediaSource.endOfStream();
                        }
                    } catch {
                        // May already be ended
                    }
                    // Restore volume for potential replay
                    audio.volume = originalVolume;
                }, 0);
            }
        }, stepMs);
    }

    /**
     * Cleanup intro MediaSource and revoke URL
     */
    private cleanupIntroMediaSource() {
        if (this.introSourceBuffer) {
            try {
                if (
                    this.introMediaSource?.readyState === "open" &&
                    !this.introSourceBuffer.updating
                ) {
                    this.introMediaSource.removeSourceBuffer(
                        this.introSourceBuffer,
                    );
                }
            } catch {
                // Ignore cleanup errors
            }
            this.introSourceBuffer = null;
        }

        if (this.introMediaSource) {
            if (this.introAudio.src) {
                URL.revokeObjectURL(this.introAudio.src);
            }
            this.introAudio.src = "";
            this.introMediaSource = null;
        }
    }

    /**
     * Cleanup main MediaSource and revoke URL
     */
    private cleanupMainMediaSource() {
        if (this.mainSourceBuffer) {
            try {
                if (
                    this.mainMediaSource?.readyState === "open" &&
                    !this.mainSourceBuffer.updating
                ) {
                    this.mainMediaSource.removeSourceBuffer(
                        this.mainSourceBuffer,
                    );
                }
            } catch {
                // Ignore cleanup errors
            }
            this.mainSourceBuffer = null;
        }

        if (this.mainMediaSource) {
            if (this.mainAudio.src) {
                URL.revokeObjectURL(this.mainAudio.src);
            }
            this.mainAudio.src = "";
            this.mainMediaSource = null;
        }
    }
}
